package songwritersnotebook.app.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import songwritersnotebook.app.data.*
import songwritersnotebook.app.utils.AudioHandler
import songwritersnotebook.app.utils.ChordUtils
import songwritersnotebook.app.utils.NetworkUtils
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.launch
import kotlinx.coroutines.isActive
import kotlinx.coroutines.Job
import kotlinx.coroutines.cancel
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.withContext
import kotlinx.coroutines.yield
import kotlinx.coroutines.flow.filterNotNull
import songwritersnotebook.app.di.PlatformDependencyProvider
import kotlinx.datetime.Clock
import songwritersnotebook.app.database.SongRepository
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.graphics.Path
import songwritersnotebook.app.utils.PlaybackState
import songwritersnotebook.app.utils.StudioPlaybackEngine
import songwritersnotebook.app.utils.WaveformGenerator
import songwritersnotebook.app.utils.AudioSampleLoader
import songwritersnotebook.app.utils.NativeDecoder
import songwritersnotebook.app.crdt.UndoScope
import songwritersnotebook.app.tutorial.TutorialManager
import kotlin.math.roundToInt
import com.benasher44.uuid.uuid4

// --- Data classes for Studio State ---

enum class GuitarInputMode { KEYBOARD, FRETBOARD }

data class StudioSettings(
    val bpm: Int = 120,
    val isPlaying: Boolean = false,
    val playheadPosition: Float = 0f,
    val timeSignature: TimeSignature = TimeSignature(),
    val isSnappingOn: Boolean = false,
    val snapInterval: SnapInterval = SnapInterval(),
    val zoomX: Float = 1.0f,
    val zoomY: Float = 1.0f
)

data class StepInterval(
    val base: String = "beat", // "beat" or "bar"
    val numerator: Int = 1,
    val denominator: Int = 4
)

enum class StepIntervalType { LENGTH, INTERVAL }

enum class DragType {
    MOVE,
    RESIZE_START,
    RESIZE_END
}

data class DragItemState(
    val id: String,
    val initialBeat: Float,
    val initialDuration: Float
)

data class DragState(
    val type: DragType,
    val items: List<DragItemState>
)

data class RecordingInfo(val id: String, val name: String, val url: String, val durationMs: Long = 0L)

data class StudioUiState(
    val song: Song? = null,
    val tracks: List<Track> = emptyList(),
    val settings: StudioSettings = StudioSettings(),
    val activeTab: String = "Lyrics",
    val itemToSet: TrackItem? = null,
    val keyboardOctave: Int = 4,
    val selectedNotes: List<String> = emptyList(),
    val selectedGuitarPositions: Set<Pair<Int, Int>> = emptySet(),
    val noteLength: StepInterval = StepInterval(numerator = 1, denominator = 1),
    val noteInterval: StepInterval = StepInterval(numerator = 1, denominator = 1),
    val editingInterval: StepIntervalType? = null,
    val selectedTrackItemIds: Set<String> = emptySet(),
    val showSettingsDialog: Boolean = false,
    val isMetronomeOn: Boolean = false,
    val isMetronomeMuted: Boolean = false,
    val isTempoPreviewActive: Boolean = false,
    val currentMetronomeBeat: Int = -1,
    val snapInterval: StepInterval = StepInterval(base = "beat", numerator = 1, denominator = 4),
    val dragState: DragState? = null,
    val instrument: String = "piano",
    val recordingsWithNames: List<RecordingInfo> = emptyList(),
    val playingRecordingId: String? = null,
    val orderedChordNames: List<String> = emptyList(),
    val chordsInUse: Set<String> = emptySet(),
    val favoriteChords: List<SavedChord> = emptyList(),
    val favoriteProgressions: List<songwritersnotebook.app.data.SavedProgression> = emptyList(),
    val chordCreatorUiState: ChordCreatorUiState = ChordCreatorUiState(),
    val guitarInputMode: GuitarInputMode = GuitarInputMode.KEYBOARD,
    val fretWindowStart: Int = 0,
    val isFavorited: Boolean = false,
    val selectedFavoriteChord: SavedChord? = null,
    val waveformCache: Map<String, List<Float>> = emptyMap(),
    val isLoadingSamples: Boolean = false,
    val sampleLoadingProgress: Float = 0f,
    val saveStatus: SaveStatus = SaveStatus.IDLE,
    val userProfile: UserProfile? = null,
    val isLassoModeActive: Boolean = false,
    val lassoPath: List<Offset> = emptyList(),
    val trackItemLayouts: Map<String, Rect> = emptyMap(),
    val canUndo: Boolean = false,
    val canRedo: Boolean = false,
    val isLeftHanded: Boolean = false,
    val trackSettingsToOpen: String? = null,
    val isViewer: Boolean = false,
    val activeTrackId: String? = null,
    val activeTrackItemId: String? = null,
    val syncStatusMessage: String? = null,
    val isLoading: Boolean = false,
    val presence: Map<String, UserPresence> = emptyMap(),
    val deviceId: String = "",
    val isPreparing: Boolean = false,
    val shouldExit: Boolean = false
)

interface UndoableCommand {
    fun execute()
    fun undo()
}

class StudioViewModel : ViewModel() {
    private val observerId = com.benasher44.uuid.uuid4().toString()
    private val songRepository: SongRepository? = PlatformDependencyProvider().getSongRepository()
    private val preferencesManager = songwritersnotebook.app.utils.PreferencesManager()
    val deviceId = preferencesManager.deviceId
    val authUserId: String? get() = songRepository?.getCurrentUserId()
    val sessionDeviceId = songRepository?.sessionDeviceId ?: com.benasher44.uuid.uuid4().toString()

    private val activeFieldShield = mutableMapOf<String, Long>()
    private val SHIELD_WINDOW_MS = 5000L // 5 seconds

    private var bpmDebounceJob: kotlinx.coroutines.Job? = null

    private fun shieldField(id: String) {
        activeFieldShield[id] = Clock.System.now().toEpochMilliseconds()
    }

    private fun isFieldShielded(id: String): Boolean {
        val shieldTime = activeFieldShield[id] ?: return false
        val now = Clock.System.now().toEpochMilliseconds()
        return (now - shieldTime) < SHIELD_WINDOW_MS
    }

    fun removeSession(deviceId: String) {
        viewModelScope.launch {
            songRepository?.removeSession(deviceId)
        }
    }
    private val chordLibraryRepository = PlatformDependencyProvider().getChordLibraryRepository()
    private val audioHandler = PlatformDependencyProvider().getAudioHandler()
    private val playbackState = PlaybackState()

    private val _uiState = MutableStateFlow(StudioUiState(
        isLeftHanded = preferencesManager.isLeftHanded,
        deviceId = preferencesManager.deviceId
    ))
    val uiState: StateFlow<StudioUiState> = _uiState.asStateFlow()

    private val _strumSpeed = MutableStateFlow(1.0f)
    val strumSpeed: StateFlow<Float> = _strumSpeed.asStateFlow()

    private val _showChordCreatorSheet = MutableStateFlow(false)
    val showChordCreatorSheet: StateFlow<Boolean> = _showChordCreatorSheet.asStateFlow()

    private val _chordToEdit = MutableStateFlow<Pair<String, List<String>>?>(null)
    val chordToEdit: StateFlow<Pair<String, List<String>>?> = _chordToEdit.asStateFlow()

    private var hasUnsavedChanges = false
    private var modifiedTrackIds = mutableSetOf<String>()
    private val deletedSectionIds = mutableListOf<String>()
    private val deletedTrackIds = mutableListOf<String>()
    private val deletedTrackItemIds = mutableListOf<String>()
    private var stateVersion = 0
    private var periodicSaveJob: kotlinx.coroutines.Job? = null
    private var songObservationJob: kotlinx.coroutines.Job? = null
    private var presenceHeartbeatJob: kotlinx.coroutines.Job? = null
    private var debounceSaveJob: kotlinx.coroutines.Job? = null

    private val isCollaborationEnabled: Boolean
        get() = _uiState.value.song?.let { it.ownerId.isNotEmpty() && (it.collaborators.isNotEmpty() || it.viewers.isNotEmpty()) } == true

    private fun getUserColor(userId: String): Int {
        val profile = _uiState.value.userProfile
        if (profile?.color != null) return profile.color!!

        val generated = songwritersnotebook.app.data.UserPresence.generateUserColor(userId)
        // Persist color in profile if not set
        if (profile != null && profile.color == null) {
            val updatedProfile = profile.copy(color = generated)
            viewModelScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
                songRepository?.updateUserProfile(updatedProfile)
            }
        }
        return generated
    }

    private fun startPresenceHeartbeat(songId: String) {
        if (songId == songwritersnotebook.app.tutorial.TutorialManager.TUTORIAL_SONG_ID) return
        presenceHeartbeatJob?.cancel()
        presenceHeartbeatJob = viewModelScope.launch {
            while (isActive) {
                val state = _uiState.value
                val userId = authUserId ?: "anonymous"
                val authUser = songwritersnotebook.app.utils.AuthHandler().getCurrentUser()
                
                // ELITE PERSISTENCE: Use the last known active track from state if selection is empty
                val selectedItemId = state.selectedTrackItemIds.firstOrNull()
                val resolvedTrackId = if (selectedItemId != null) {
                    state.tracks.find { track -> track.items.any { it.id == selectedItemId } }?.id
                } else {
                    state.activeTrackId
                }

                val presence = UserPresence(
                    userId = userId,
                    email = authUser?.email ?: state.userProfile?.email ?: "",
                    username = authUser?.displayName ?: state.userProfile?.username ?: "Songwriter",
                    color = getUserColor(userId),
                    deviceId = sessionDeviceId,
                    activeTrackId = resolvedTrackId,
                    activeTrackItemId = selectedItemId ?: state.activeTrackItemId,
                    selectedTrackItemIds = state.selectedTrackItemIds,
                    lastActive = Clock.System.now().toEpochMilliseconds().toDouble()
                )
                songRepository?.updatePresence(songId, presence)
                delay(30000) // 30s heartbeat instead of 10s
            }
        }
    }

    private var presenceUpdateJob: Job? = null

    fun updatePresence(trackId: String? = null, trackItemId: String? = null) {
        val songId = _uiState.value.song?.id ?: return
        val state = _uiState.value
        val userId = authUserId ?: "anonymous"
        
        // Update local state first so heartbeat picks it up
        _uiState.update { it.copy(activeTrackId = trackId ?: it.activeTrackId, activeTrackItemId = trackItemId ?: it.activeTrackItemId) }

        if (!isCollaborationEnabled && _uiState.value.presence.isEmpty()) return

        viewModelScope.launch {
            val authUser = songwritersnotebook.app.utils.AuthHandler().getCurrentUser()
            
            // If trackId is not provided but trackItemId is, resolve it.
            val finalTrackId = if (trackId == null && trackItemId != null) {
                state.tracks.find { track -> track.items.any { it.id == trackItemId } }?.id
            } else {
                trackId
            }

            val presence = UserPresence(
                userId = userId,
                email = authUser?.email ?: state.userProfile?.email ?: "",
                username = authUser?.displayName ?: state.userProfile?.username ?: "Songwriter",
                color = getUserColor(userId),
                deviceId = sessionDeviceId,
                activeTrackId = finalTrackId ?: state.activeTrackId,
                activeTrackItemId = trackItemId ?: state.activeTrackItemId,
                selectedTrackItemIds = state.selectedTrackItemIds,
                lastActive = Clock.System.now().toEpochMilliseconds().toDouble()
            )
            
            presenceUpdateJob?.cancel()
            presenceUpdateJob = launch {
                delay(1000) // 1000ms debounce
                songRepository?.updatePresence(songId, presence)
            }
        }
    }
    private val DEBOUNCE_TIME_MS = 4000L

    private fun markAsChanged(updatedSongFromCaller: Song? = null, forceUndoStep: Boolean = false, isContentChange: Boolean = true) {
        if (isContentChange) {
            hasUnsavedChanges = true
        }
        stateVersion++
        
        // High-frequency CRDT update (Mixer/Arrangement changes)
        val songToUse = updatedSongFromCaller ?: _uiState.value.song
        songToUse?.let { song ->
            val currentState = _uiState.value
            val currentSettings = currentState.settings
            val studioState = StudioState(
                tracks = currentState.tracks,
                timeSignature = currentSettings.timeSignature,
                isMetronomeEnabled = currentState.isMetronomeOn,
                isSnapEnabled = currentSettings.isSnappingOn,
                snapInterval = currentSettings.snapInterval,
                zoomX = currentSettings.zoomX,
                zoomY = currentSettings.zoomY
            )
            // Bump the local timestamp to ensure it's "newer" than the last received cloud state
            val updatedSong = song.copy(
                studioState = studioState, 
                bpm = currentSettings.bpm,
                lastModified = kotlinx.datetime.Clock.System.now()
            )
            
            // CRITICAL: Update local UI state song immediately so Content Shield sees the new tracks/items
            _uiState.update { it.copy(song = updatedSong) }

            // Push to RTDB instantly
            if (isContentChange) println("DEBUG_SYNC_STUDIO: Pushing CRDT update for item change in track.")
            
            songRepository?.pushCrdtUpdate(
                song.id, 
                updatedSong, 
                scope = UndoScope.STUDIO, 
                forceUndoStep = forceUndoStep,
                isUndoable = isContentChange,
                shouldPushToCloud = isContentChange
            )
            
            // Trigger a proper full save (cloud vault, database, etc.) after a short delay
            if (isContentChange) {
                triggerDebouncedSave()
            }

            // Update local chords in use
            val chordsInUse = getChordsInSong(updatedSong) + getChordsInTracks(currentState.tracks)
            _uiState.update { it.copy(chordsInUse = chordsInUse) }
        }
    }

    private fun markAsTrackChanged(trackId: String, updatedSong: Song? = null, forceUndoStep: Boolean = false) {
        modifiedTrackIds.add(trackId)
        markAsChanged(updatedSong, forceUndoStep)
    }

    private fun triggerDebouncedSave() {
        debounceSaveJob?.cancel()
        debounceSaveJob = viewModelScope.launch {
            delay(DEBOUNCE_TIME_MS)
            saveStudioState(forceSave = false)
        }
    }

    private var playbackEngine: StudioPlaybackEngine? = null
    private var playbackJob: kotlinx.coroutines.Job? = null
    
    private val isViewer: Boolean
        get() = _uiState.value.song?.viewers?.contains(authUserId) == true && _uiState.value.song?.collaborators?.contains(authUserId) != true

    //region Undo/Redo System
    private var isApplyingUndoRedo = false

    fun undo() {
        if (isViewer) return
        val currentSong = _uiState.value.song ?: return
        viewModelScope.launch {
            isApplyingUndoRedo = true
            hasUnsavedChanges = false // Clear local protection to allow adoption of stack state
            songRepository?.undo(currentSong, UndoScope.STUDIO)
            yield() // Give collector a chance to process the new flow value while isApplyingUndoRedo is still true
            isApplyingUndoRedo = false
        }
    }

    fun redo() {
        if (isViewer) return
        val currentSong = _uiState.value.song ?: return
        viewModelScope.launch {
            isApplyingUndoRedo = true
            hasUnsavedChanges = false // Clear local protection to allow adoption of stack state
            songRepository?.redo(currentSong, UndoScope.STUDIO)
            yield() // Give collector a chance to process the new flow value while isApplyingUndoRedo is still true
            isApplyingUndoRedo = false
        }
    }

    internal fun addTrackItemsInternal(itemsToAdd: List<TrackItem>) {
        var trackIdToMark: String? = null
        var updatedSongState: Song? = null
        
        // Sanitize recording URLs: Never save absolute local paths to CRDT
        val sanitizedItems = itemsToAdd.map { item ->
            if (item.type == "recording" && (item.data.url?.startsWith("/") == true || item.data.url?.contains("data/user") == true)) {
                // If it's a local path, we strip it. processSongUpdate will re-resolve it from recordingsWithNames.
                item.copy(data = item.data.copy(url = null))
            } else item
        }

        _uiState.update { currentState ->
            if (isViewer) return@update currentState
            if (sanitizedItems.isEmpty()) return@update currentState

            val trackToUpdateId = currentState.tracks.find { track ->
                isValidPlacement(sanitizedItems.first(), track.name)
            }?.id ?: return@update currentState
            trackIdToMark = trackToUpdateId

            val updatedTracks = currentState.tracks.map { track ->
                if (track.id == trackToUpdateId) {
                    track.copy(items = track.items + sanitizedItems)
                } else {
                    track
                }
            }
            // User requested: No auto-selection of new items
            val nextState = currentState.copy(tracks = updatedTracks)
            updatedSongState = nextState.song // This might still be slightly stale if not updated in state yet, but better than nothing
            nextState
        }
        trackIdToMark?.let { markAsTrackChanged(it, updatedSongState, forceUndoStep = true) }
    }

    internal fun deleteTrackItemsInternal(idsToDelete: List<String>) {
        var changedIds = setOf<String>()
        var updatedSongState: Song? = null
        _uiState.update { currentState ->
            if (isViewer) return@update currentState
            if (idsToDelete.isEmpty()) return@update currentState
            
            // Track deletions for sync
            deletedTrackItemIds.addAll(idsToDelete)
            
            val changedTrackIds = mutableSetOf<String>()
            val updatedTracks = currentState.tracks.map { track ->
                val itemsToDeleteInThisTrack = track.items.filter { idsToDelete.contains(it.id) }
                if (itemsToDeleteInThisTrack.isNotEmpty()) {
                    changedTrackIds.add(track.id)
                    // If it's the lyrics track, also track these as line deletions
                    if (track.id == "lyrics-track") {
                        deletedSectionIds.addAll(itemsToDeleteInThisTrack.map { it.id }) // Wait, lyrics track items ARE Lines.
                    }
                }
                val newItems = track.items.filterNot { idsToDelete.contains(it.id) }
                track.copy(items = newItems)
            }
            changedIds = changedTrackIds
            
            val updatedRecordings = currentState.song?.recordings?.toMutableMap()?.apply {
                idsToDelete.forEach { remove(it) }
            }
            val newSong = currentState.song?.copy(recordings = updatedRecordings ?: emptyMap())
            updatedSongState = newSong
            
            currentState.copy(tracks = updatedTracks, song = newSong)
        }
        changedIds.forEach { markAsTrackChanged(it, updatedSongState, forceUndoStep = true) }
    }

    internal fun moveTrackItemsInternal(itemIds: List<String>, distanceInBeats: Float) {
        if (distanceInBeats.isNaN() || distanceInBeats.isInfinite()) return
        val changedIds = mutableSetOf<String>()
        var updatedSongState: Song? = null
        _uiState.update { currentState ->
            if (isViewer) return@update currentState
            val changedTrackIds = mutableSetOf<String>()
            val updatedTracks = currentState.tracks.map { track ->
                var trackChanged = false
                val newItems = track.items.map { item ->
                    if (itemIds.contains(item.id)) {
                        trackChanged = true
                        item.copy(beat = (item.beat + distanceInBeats).coerceAtLeast(0f))
                    } else {
                        item
                    }
                }
                if (trackChanged) changedTrackIds.add(track.id)
                track.copy(items = newItems)
            }
            changedIds.addAll(changedTrackIds)
            updatedSongState = currentState.song
            currentState.copy(tracks = updatedTracks)
        }
        changedIds.forEach { markAsTrackChanged(it, updatedSongState, forceUndoStep = true) }
    }

    internal fun resizeTrackItemInternal(itemId: String, beatDelta: Float, durationDelta: Float, trimStartDelta: Float) {
        if (beatDelta.isNaN() || beatDelta.isInfinite() || 
            durationDelta.isNaN() || durationDelta.isInfinite() || 
            trimStartDelta.isNaN() || trimStartDelta.isInfinite()) return
            
        var changedId: String? = null
        var updatedSongState: Song? = null
        _uiState.update { currentState ->
            if (isViewer) return@update currentState
            var changedTrackId: String? = null
            val updatedTracks = currentState.tracks.map { track ->
                val newItems = track.items.map { item ->
                    if (item.id == itemId) {
                        changedTrackId = track.id
                        val newTrim = if (item.type == "recording") {
                            (item.trimStartBeats + trimStartDelta).coerceAtLeast(0f)
                        } else {
                            item.trimStartBeats
                        }
                        item.copy(
                            beat = (item.beat + beatDelta).coerceAtLeast(0f),
                            duration = (item.duration + durationDelta).coerceAtLeast(0.1f),
                            trimStartBeats = newTrim
                        )
                    } else {
                        item
                    }
                }
                track.copy(items = newItems)
            }
            changedId = changedTrackId
            updatedSongState = currentState.song
            currentState.copy(tracks = updatedTracks)
        }
        changedId?.let { markAsTrackChanged(it, updatedSongState, forceUndoStep = true) }
    }
    //endregion

    private var songId: String? = null
    private var isSyncingRecordings = false
    private var saveStatusJob: Job? = null
    private val networkUtils = songwritersnotebook.app.utils.NetworkUtils()
    private var preOfflineStatus: SaveStatus? = null

    init {
        startPeriodicSaveTimer()
        observeUndoRedoStatus()
        
        // Monitor Network Status
        viewModelScope.launch {
            networkUtils.networkStatus.collect { isOnline: Boolean ->
                _uiState.update { state ->
                    if (!isOnline) {
                        if (state.saveStatus != SaveStatus.OFFLINE) {
                            preOfflineStatus = state.saveStatus
                        }
                        state.copy(saveStatus = SaveStatus.OFFLINE)
                    } else {
                        val restoredStatus = preOfflineStatus ?: state.saveStatus
                        val nextStatus = if (state.saveStatus == SaveStatus.OFFLINE) {
                            if (hasUnsavedChanges) SaveStatus.SYNCING else SaveStatus.IDLE
                        } else {
                            state.saveStatus
                        }
                        state.copy(saveStatus = nextStatus)
                    }
                }
            }
        }

        viewModelScope.launch {
            chordLibraryRepository?.favoriteChords?.collect { favorites ->
                _uiState.update { it.copy(favoriteChords = favorites) }
            }
        }
        
        viewModelScope.launch {
            chordLibraryRepository?.favoriteProgressions?.collect { favorites ->
                _uiState.update { it.copy(favoriteProgressions = favorites) }
            }
        }
        // Monitor selection
        viewModelScope.launch {
            _uiState.map { it.selectedTrackItemIds }
                .distinctUntilChanged() // Only trigger when selection set actually changes
                .collect { selectedIds ->
                    // updateTrackLocks(selectedIds)
                }
        }
        viewModelScope.launch {
            songRepository?.currentUserProfile?.collect { profile ->
                _uiState.update { it.copy(userProfile = profile) }
            }
        }
    }

    private fun monitorSaveStatus(songId: String) {
        saveStatusJob?.cancel()
        saveStatusJob = viewModelScope.launch {
            if (songId == songwritersnotebook.app.tutorial.TutorialManager.TUTORIAL_SONG_ID) return@launch
            
            combine(
                songRepository?.pendingDeltasCount ?: MutableStateFlow(0),
                songRepository?.getBakeStatus(songId) ?: flowOf("idle")
            ) { pending: Int, bake: String? ->
                Pair(pending, bake)
            }.collect { (pending, bake) ->
                val isBaking = bake == "pending"
                val isSyncing = pending > 0
                
                _uiState.update { state ->
                    val newStatus = when {
                        state.saveStatus == SaveStatus.SAVING -> SaveStatus.SAVING
                        isSyncing || isBaking -> SaveStatus.SYNCING
                        state.saveStatus == SaveStatus.SYNCING -> {
                            // Sync finished, but we no longer reset hasUnsavedChanges here.
                            // We keep it true to protect local state from 
                            // being overwritten by incoming cloud updates until 
                            // a formal saveStudioState() checkpoint is completed.
                            SaveStatus.SAVED
                        }
                        else -> state.saveStatus
                    }
                    
                    // Auto-hide "Saved" after a delay
                    if (newStatus == SaveStatus.SAVED && state.saveStatus != SaveStatus.SAVED) {
                        viewModelScope.launch {
                            delay(3000)
                            _uiState.update { if (it.saveStatus == SaveStatus.SAVED && !hasUnsavedChanges) it.copy(saveStatus = SaveStatus.IDLE) else it }
                        }
                    }
                    
                    state.copy(saveStatus = newStatus)
                }
            }
        }
    }

    private fun observeUndoRedoStatus() {
        viewModelScope.launch {
            songRepository?.canStudioUndo?.collect { can ->
                _uiState.update { it.copy(canUndo = can) }
            }
        }
        viewModelScope.launch {
            songRepository?.canStudioRedo?.collect { can ->
                _uiState.update { it.copy(canRedo = can) }
            }
        }
    }

    fun loadSongById(id: String) {
        // CRITICAL: Stop previous observation and clean up repository session
        val previousSongId = _uiState.value.song?.id
        if (previousSongId != null && previousSongId != id) {
            songRepository?.stopSongObservation(previousSongId, authUserId, observerId)
        }

        this.songId = id
        songRepository?.registerObserver(observerId)
        songObservationJob?.cancel()
        debounceSaveJob?.cancel() // Cancel any pending saves for the OLD song

        startPresenceHeartbeat(id)
        monitorSaveStatus(id)        
        songObservationJob = viewModelScope.launch {
            if (id == TutorialManager.TUTORIAL_SONG_ID) {
                // Tutorial song is local only. Fetch directly first to avoid hanging on flow initialization.
                val tutorialSong = songRepository?.getSongById(id)
                if (tutorialSong != null) {
                    updateStudioStateFromSong(tutorialSong)
                }
                // Also start observing in case it updates, but the direct fetch ensures it loads immediately.
                loadSongAndInitializeTracks()
                return@launch
            }

            // Force a targeted cloud refresh for this specific song.
            songRepository?.refreshSongFromCloud(id)
            
            val initialSong = songRepository?.getSongById(id)

            // AUTO-HEAL: If the song was left in a sync error state, try to save it now
            if (initialSong?.isSyncError == true) {
                println("DEBUG_VM: Studio auto-retrying sync for song in error state: ${initialSong.title}")
                songRepository?.saveSongNonBlocking(initialSong, scope = UndoScope.STUDIO, isUndoable = false)
            }

            val isCloud = initialSong?.ownerId?.isNotEmpty() == true
            
            if (isCloud) {
                println("DEBUG_DIAG: Studio observing as Cloud Song (Real-time stitching)")
                // Real-time Cloud Observation (Normalized Sub-collections)
                songRepository?.observeSong(id)?.collect { cloudSong ->
                    if (cloudSong != null) {
                        handleSongUpdate(cloudSong)
                    } else if (_uiState.value.song == null) {
                        // Only if we are truly empty and it's the start, we can accept null (loading state)
                    } else {
                        // ACCESS REVOKED: Song became null while we were editing
                        println("DEBUG_VM: Studio access revoked for $id during active edit. Kicking out.")
                        audioHandler.stopAllTrackRecordings()
                        playbackJob?.cancel()
                        _uiState.update { it.copy(shouldExit = true, settings = it.settings.copy(isPlaying = false)) }
                    }
                }
            } else {
                // Local/Legacy Observation
                loadSongAndInitializeTracks()
            }
        }
    }

    private fun handleSongUpdate(song: Song) {
        viewModelScope.launch {
            val currentState = _uiState.value
            val now = kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
            val currentDeviceId = sessionDeviceId
            
            // 1. Reactive Presence (Standardized 60s window)
            val filteredPresence = song.presence.filter { 
                it.value.deviceId != currentDeviceId && (now - it.value.lastActive) < 60000 
            }

            // 2. Auth-Aware Role Detection
            val uid = authUserId
            val isActuallyViewer = uid != null && song.ownerId != uid && !song.collaborators.contains(uid)

            // 3. Arrangement Protection (Dragging only)
            val isDragging = currentState.dragState != null
            val isModalOpen = currentState.showSettingsDialog || currentState.editingInterval != null
            
            if (isDragging || isModalOpen) {
                _uiState.update { state ->
                    state.copy(
                        presence = filteredPresence,
                        isViewer = isActuallyViewer,
                        song = state.song?.copy(
                            ownerId = song.ownerId,
                            ownerUsername = song.ownerUsername,
                            collaborators = song.collaborators,
                            viewers = song.viewers,
                            presence = filteredPresence
                        )
                    )
                }
                return@launch
            }

            // 4. Adopt Merged Cloud State
            updateStudioStateFromSong(song.copy(presence = filteredPresence))
        }
    }

    private fun generateWaveformForItem(recordingInfo: RecordingInfo) {
        viewModelScope.launch {
            try {
                if (recordingInfo.url.isBlank()) return@launch
                
                // 1. Check memory cache
                if (_uiState.value.waveformCache.containsKey(recordingInfo.url)) {
                     return@launch
                }

                // 2. Check disk cache (Database)
                val cached = songRepository?.getWaveform(recordingInfo.url)
                val result = if (cached != null) {
                    cached
                } else {
                    val genResult = WaveformGenerator.generateWaveform(recordingInfo.url)
                    // 3. Save to disk cache for next time
                    songRepository?.saveWaveform(recordingInfo.url, genResult)
                    genResult
                }

                _uiState.update { currentState ->
                    val newCache = currentState.waveformCache.toMutableMap()
                    newCache[recordingInfo.url] = result.points

                    // 4. Update song metadata if duration was missing
                    if (recordingInfo.durationMs == 0L && result.durationMillis > 0) {
                        val currentSong = currentState.song
                        if (currentSong != null) {
                            val updatedRecordings = currentSong.recordings.mapValues { (_, list) ->
                                list.map { if (it.id == recordingInfo.id) it.copy(durationMs = result.durationMillis) else it }
                            }
                            if (updatedRecordings != currentSong.recordings) {
                                val updatedSong = currentSong.copy(recordings = updatedRecordings)
                                viewModelScope.launch {
                                    _uiState.update { it.copy(song = updatedSong) }
                                    markAsChanged(forceUndoStep = true)
                                }
                            }
                        }
                    }

                    val currentItemToSet = currentState.itemToSet
                    if (currentItemToSet != null &&
                        currentItemToSet.type == "recording" &&
                        currentItemToSet.data.url == recordingInfo.url
                    ) {
                         val beatsPerSecond = currentState.settings.bpm / 60f
                         val durationInSeconds = result.durationMillis / 1000f
                         val durationInBeats = durationInSeconds * beatsPerSecond
                         currentState.copy(
                             waveformCache = newCache,
                             itemToSet = if (durationInBeats > 0) currentItemToSet.copy(duration = durationInBeats) else currentItemToSet
                         )
                    } else {
                        currentState.copy(waveformCache = newCache)
                    }
                }
            } catch (e: Exception) {
                println("Error generating waveform: $e")
            }
        }
    }

    private fun loadSongAndInitializeTracks() {
        val repo = songRepository ?: return
        viewModelScope.launch {
            combine(repo.songs, repo.sharedSongs) { songs, shared -> 
                val localSong = songs.find { it.id == songId && it.ownerId == authUserId }
                val sharedSong = shared.find { it.id == songId }
                
                if (localSong != null && sharedSong != null) {
                    if (localSong.lastModified > sharedSong.lastModified) listOf(localSong) + shared else listOf(sharedSong) + songs
                } else if (sharedSong != null) listOf(sharedSong) + songs
                else if (localSong != null) songs + shared
                else emptyList()
            }
            .map { songs -> songs.find { it.id == songId } }
            .filterNotNull()
            .collect { song ->
                updateStudioStateFromSong(song)
                
                // Sync Audio
                viewModelScope.launch(songwritersnotebook.app.AppDispatchers.IO) {
                    songRepository?.syncRecordingsForSong(song) { msg ->
                        _uiState.update { it.copy(syncStatusMessage = msg.ifBlank { null }) }
                    }
                }
            }
        }
    }

    private suspend fun updateStudioStateFromSong(song: Song) {
        val repo = songRepository ?: return
        val recordingsWithNames = mutableListOf<RecordingInfo>()
        
        song.sections.forEach { section ->
            song.recordings[section.id]?.forEach { recording ->
                val finalUrl = repo.getResolvedRecordingUrl(recording.id, recording.filePath)
                recordingsWithNames.add(RecordingInfo(recording.id, "${section.title} - ${recording.label}", finalUrl, recording.durationMs))
            }
            section.lines.forEachIndexed { idx, line ->
                song.recordings[line.id]?.forEach { recording ->
                    val finalUrl = repo.getResolvedRecordingUrl(recording.id, recording.filePath)
                    recordingsWithNames.add(RecordingInfo(recording.id, "${section.title} - Line ${idx + 1} - ${recording.label}", finalUrl, recording.durationMs))
                }
            }
        }

        val loadedStudioState = song.studioState ?: StudioState()

        // 1. Define the Strict Canonical Order
        val defaultTrackOrder = listOf("lyrics-track", "vocals-track", "guitar-track", "piano-track", "bass-track", "drums-track")
        val defaultTracks = listOf(
            Track(id = "lyrics-track", name = "Lyrics"),
            Track(id = "vocals-track", name = "Vocals", volume = 0.25f),
            Track(id = "guitar-track", name = "Guitar"),
            Track(id = "piano-track", name = "Piano"),
            Track(id = "bass-track", name = "Bass"),
            Track(id = "drums-track", name = "Drums")
        )

        val tracksFromSong = loadedStudioState.tracks
        val enforcedTracks = defaultTrackOrder.map { trackId ->
            tracksFromSong.find { it.id == trackId } ?: defaultTracks.find { it.id == trackId }!!
        }
        val extraTracks = tracksFromSong.filter { it.id !in defaultTrackOrder }
        val finalTracks = (enforcedTracks + extraTracks).map { track ->
            val currentLocalTrack = _uiState.value.tracks.find { it.id == track.id }
            
            if (modifiedTrackIds.contains(track.id) && currentLocalTrack != null) {
                // Shield this track from remote updates while we have pending local changes
                currentLocalTrack
            } else {
                track.copy(
                    isMuted = currentLocalTrack?.isMuted ?: false,
                    isSoloed = currentLocalTrack?.isSoloed ?: false,
                    items = track.items.map { item ->
                        if (item.type == "recording") {
                            val info = recordingsWithNames.find { it.id == item.data.id || (it.url.isNotEmpty() && it.url == item.data.url) }
                            if (info != null) item.copy(data = item.data.copy(url = info.url, id = info.id)) else item
                        } else item
                    }
                )
            }
        }

        val orderedChords = songwritersnotebook.app.utils.ChordUtils.getOrderedChordsInSong(song)
        val chordsInUse = getChordsInSong(song) + getChordsInTracks(finalTracks)
        val uid = authUserId
        val isActuallyViewer = uid != null && song.ownerId != uid && !song.collaborators.contains(uid)

        _uiState.update { currentState ->
            currentState.copy(
                song = song,
                tracks = finalTracks,
                presence = song.presence,
                settings = currentState.settings.copy(
                    bpm = if (isFieldShielded("song_bpm")) currentState.settings.bpm else song.bpm,
                    timeSignature = loadedStudioState.timeSignature
                ),
                recordingsWithNames = recordingsWithNames,
                orderedChordNames = orderedChords,
                chordsInUse = chordsInUse,
                isViewer = isActuallyViewer,
                chordCreatorUiState = currentState.chordCreatorUiState.copy(
                    guitarTuning = song.guitarTuning,
                    guitarCapo = song.guitarCapo
                )
            )
        }
        
        viewModelScope.launch(Dispatchers.Default) {
            recordingsWithNames.forEach { audioHandler.cacheRecordingPcm(it.url) }
        }

        // Trigger waveform generation for visible items
        finalTracks.forEach { track ->
            track.items.forEach { item ->
                if (item.type == "recording") {
                    val info = recordingsWithNames.find { it.url == item.data.url }
                    if (info != null && !_uiState.value.waveformCache.containsKey(info.url)) {
                        generateWaveformForItem(info)
                    }
                }
            }
        }
    }

    private fun syncRecordings() {
        // Handled within loadSongAndInitializeTracks in this KMP version
    }

    private fun startPeriodicSaveTimer() {
        periodicSaveJob?.cancel()
        periodicSaveJob = viewModelScope.launch {
            while(true) {
                delay(300000L) // Auto-save every 5 minutes (temporarily increased for debugging)
                saveStudioState(forceSave = false)
            }
        }
    }

    private fun saveStudioState(forceSave: Boolean = false) {
        if (isViewer) return
        if (_uiState.value.saveStatus == SaveStatus.SAVING) return
        if (!forceSave && !hasUnsavedChanges) return

        val currentSong = _uiState.value.song ?: return
        
        _uiState.update { it.copy(saveStatus = SaveStatus.SAVING) }
        
        val currentState = _uiState.value
        val currentSettings = currentState.settings
        val versionAtStart = stateVersion
        
        val studioState = StudioState(
            tracks = currentState.tracks,
            timeSignature = currentSettings.timeSignature,
            isMetronomeEnabled = currentState.isMetronomeOn,
            isSnapEnabled = currentSettings.isSnappingOn,
            snapInterval = currentSettings.snapInterval,
            zoomX = currentSettings.zoomX,
            zoomY = currentSettings.zoomY
        )
        
        val updatedSong = currentSong.copy(
            studioState = studioState,
            bpm = currentSettings.bpm,
            guitarTuning = currentState.chordCreatorUiState.guitarTuning,
            guitarCapo = currentState.chordCreatorUiState.guitarCapo,
            lastModified = currentSong.lastModified // Don't bump timestamp here, repo/server handles it
        )

        viewModelScope.launch {
            try {
                val response = songRepository?.saveSongToAllSources(
                    song = updatedSong,
                    scope = UndoScope.STUDIO,
                    deletedSectionIds = deletedSectionIds.toList(),
                    deletedTrackIds = deletedTrackIds.toList(),
                    deletedTrackItemIds = deletedTrackItemIds.toList()
                )

                if (response?.status == "success") {
                    println("DEBUG_VM: Studio save successful.")
                    val currentSongInState = _uiState.value.song
                    if (currentSongInState?.id == updatedSong.id) {
                        // Check if the song in state is still the one we just saved (or has been updated by the repo to match)
                        if (currentSongInState.lastModified == updatedSong.lastModified) {
                            hasUnsavedChanges = false
                            // Clear deletions on success
                            deletedSectionIds.clear()
                            deletedTrackIds.clear()
                            deletedTrackItemIds.clear()
                            modifiedTrackIds.clear()

                            _uiState.update { state -> 
                                state.copy(
                                    saveStatus = SaveStatus.SAVED,
                                    song = state.song?.copy(revisionId = response.revisionId ?: state.song.revisionId)
                                ) 
                            }
                        } else {
                            println("DEBUG_VM: New studio changes during save ignored, relying on CRDT stream.")
                        }
                    }
                    delay(2000L)
                    _uiState.update { if (it.saveStatus == SaveStatus.SAVED && !hasUnsavedChanges) it.copy(saveStatus = SaveStatus.IDLE) else it }
                } else {
                    println("DEBUG_VM: Studio save FAILED: ${response?.message}")
                    _uiState.update { it.copy(saveStatus = SaveStatus.ERROR) }
                }
            } catch (e: Exception) {
                println("DEBUG_VM: Studio save exception: $e")
                _uiState.update { it.copy(saveStatus = SaveStatus.ERROR) }
            }
        }
    }

    fun saveSong() {
        saveStudioState(forceSave = true)
    }

    fun onOpenTrackSettings(trackId: String) {
        _uiState.update { it.copy(trackSettingsToOpen = trackId) }
    }

    fun onDismissTrackSettings() {
        _uiState.update { it.copy(trackSettingsToOpen = null) }
    }

    fun onSettingsClicked() {
        _uiState.update { it.copy(showSettingsDialog = true) }
    }

    fun onSettingsDismiss() {
        _uiState.update { it.copy(showSettingsDialog = false) }
        stopTempoPreview()
        saveStudioState(forceSave = true)
    }

    fun toggleTempoPreview() {
        val newValue = !_uiState.value.isTempoPreviewActive
        _uiState.update { it.copy(isTempoPreviewActive = newValue) }
        
        if (newValue) {
            val bpm = _uiState.value.settings.bpm
            audioHandler.startMetronome(bpm) { beat ->
                viewModelScope.launch { updateMetronomeBeat(beat) }
            }
        } else {
            stopTempoPreview()
        }
    }

    fun stopTempoPreview() {
        audioHandler.stopMetronome()
        _uiState.update { it.copy(isTempoPreviewActive = false, currentMetronomeBeat = -1) }
    }

    fun stopMetronome() {
        _uiState.update { it.copy(isMetronomeOn = false, currentMetronomeBeat = -1) }
    }

    private fun initializePlaybackEngine() {
        if (playbackEngine == null) {
            playbackEngine = StudioPlaybackEngine(
                uiStateFlow = _uiState,
                audioHandler = audioHandler,
                playbackState = playbackState,
                getChordLibrary = { _uiState.value.song?.chordDefinitions ?: emptyMap() },
                onBeat = { beat -> updateMetronomeBeat(beat) }
            )
        }
    }

    suspend fun warmUp() {
        initializePlaybackEngine()
        playbackEngine?.warmUp()
    }

    fun onPlayPauseClicked() {
        initializePlaybackEngine()
        val engine = playbackEngine ?: return
        
        if (engine.isPlaying()) {
            engine.stop()
            playbackJob?.cancel()
            playbackJob = null
            _uiState.update { it.copy(settings = it.settings.copy(isPlaying = false)) }
        } else {
            _uiState.update { it.copy(settings = it.settings.copy(isPlaying = true)) }
            
            engine.play(onPreparingChanged = { preparing ->
                _uiState.update { it.copy(isPreparing = preparing) }
            })
            playbackJob = viewModelScope.launch {
                while (isActive && engine.isPlaying()) {
                    val currentBeat = playbackState.getCurrentBeat()
                    _uiState.update { 
                        it.copy(settings = it.settings.copy(playheadPosition = currentBeat)) 
                    }
                    delay(16)
                }
                _uiState.update { it.copy(settings = it.settings.copy(isPlaying = false)) }
            }
        }
    }

    fun onRewindClicked() {
        playbackEngine?.stop()
        playbackJob?.cancel()
        playbackJob = null
        playbackState.setCurrentBeat(0f)
        _uiState.update {
            it.copy(settings = it.settings.copy(playheadPosition = 0f, isPlaying = false))
        }
    }

    fun onFastForwardClicked() {
        playbackEngine?.stop()
        playbackJob?.cancel()
        playbackJob = null
        val lastBeat = _uiState.value.tracks.flatMap { it.items }.maxOfOrNull { it.beat + it.duration } ?: 0f
        playbackState.setCurrentBeat(lastBeat)
        _uiState.update {
            it.copy(settings = it.settings.copy(playheadPosition = lastBeat, isPlaying = false))
        }
    }

    fun onPlayheadSeek(newPosition: Float) {
        playbackEngine?.stop()
        playbackJob?.cancel()
        playbackJob = null
        val snappedPosition = applySnapping(newPosition)
        playbackState.setCurrentBeat(snappedPosition)
        _uiState.update {
            it.copy(settings = it.settings.copy(playheadPosition = snappedPosition, isPlaying = false))
        }
    }

    fun onTabSelected(tab: String) {
        _uiState.update { it.copy(activeTab = tab) }
        updatePresence()
    }

    fun setInstrument(instrument: String) {
        _uiState.update { state ->
            val newItemToSet = if (state.itemToSet?.type == "chord") {
                state.itemToSet.copy(data = state.itemToSet.data.copy(instrument = instrument))
            } else if (state.itemToSet?.type == "note") {
                null // Clear note selections because positions won't map correctly across instruments
            } else {
                state.itemToSet
            }
            state.copy(
                instrument = instrument,
                itemToSet = newItemToSet,
                selectedNotes = emptyList(),
                selectedGuitarPositions = emptySet()
            ) 
        }
        updatePresence()
    }

    private fun isValidPlacement(item: TrackItem, trackName: String): Boolean {
        val lowerTrackName = trackName.lowercase()
        return when (item.type) {
            "lyric" -> lowerTrackName == "lyrics"
            "recording" -> lowerTrackName.contains("vocal")
            "chord" -> lowerTrackName.contains(item.data.instrument ?: "")
            "note" -> lowerTrackName.contains(item.data.instrument ?: "")
            else -> false
        }
    }

    fun onSnapIntervalChange(newInterval: StepInterval) {
        _uiState.update {
            it.copy(
                snapInterval = newInterval,
                settings = it.settings.copy(
                    snapInterval = SnapInterval(
                        base = newInterval.base,
                        numerator = newInterval.numerator,
                        denominator = newInterval.denominator
                    )
                )
            )
        }
        markAsChanged(isContentChange = false)
    }

    private fun calculateSnapIntervalInBeats(): Float? {
        val settings = _uiState.value.settings
        val snapInterval = settings.snapInterval

        if (!settings.isSnappingOn || snapInterval.denominator <= 0) return null

        val ratio = snapInterval.numerator.toFloat() / snapInterval.denominator.toFloat()
        if (snapInterval.base == "bar") return ratio * settings.timeSignature.beats
        return ratio
    }

    private fun applySnapping(value: Float): Float {
        val snapInterval = calculateSnapIntervalInBeats() ?: return value
        if (snapInterval <= 0f) return value
        return (value / snapInterval).roundToInt() * snapInterval
    }

    fun armItemForPlacement(item: TrackItem) {
        _uiState.update {
            val current = it.itemToSet
            val isSame = when {
                current == null -> false
                current.type != item.type -> false
                current.type == "recording" -> current.data.id == item.data.id
                current.type == "chord" -> current.data.name == item.data.name
                current.type == "lyric" -> current.data.text == item.data.text && current.data.section == item.data.section
                current.type == "note" -> current.data.notes == item.data.notes
                else -> false
            }
            
            if (isSame) {
                it.copy(itemToSet = null)
            } else {
                it.copy(itemToSet = item)
            }
        }
        
        // Update presence to show which track the item belongs to
        val track = _uiState.value.tracks.find { t -> isValidPlacement(item, t.name) }
        updatePresence(trackId = track?.id)
    }

    fun forceArmNote(noteName: String) {
        _uiState.update {
            val item = TrackItem(
                id = uuid4().toString(),
                beat = 0f,
                duration = 1f,
                type = "note",
                data = TrackData(
                    notes = listOf(noteName),
                    instrument = it.instrument,
                    name = noteName
                )
            )
            it.copy(
                selectedNotes = listOf(noteName),
                selectedGuitarPositions = emptySet(),
                itemToSet = item
            )
        }
    }

    fun forceArmChord(chordName: String) {
        _uiState.update {
            val item = TrackItem(
                id = uuid4().toString(),
                beat = 0f,
                duration = 1f,
                type = "chord",
                data = TrackData(
                    name = chordName,
                    instrument = it.instrument
                )
            )
            it.copy(
                itemToSet = item
            )
        }
    }

    fun onNoteClicked(noteName: String, guitarPosition: Pair<Int, Int>? = null) {
        audioHandler.playNote(noteName, _uiState.value.instrument)
        _uiState.update {
            val currentSelectedNotes = it.selectedNotes
            val currentSelectedPositions = it.selectedGuitarPositions
            
            val newSelectedNotes: List<String>
            val newSelectedPositions: Set<Pair<Int, Int>>

            if (guitarPosition != null) {
                // Guitar Input Mode: Toggle specific position
                if (currentSelectedPositions.contains(guitarPosition)) {
                    newSelectedPositions = currentSelectedPositions - guitarPosition
                    // If we remove the last position for this note name, remove the note name too
                    // But wait, if we have multiple C4s selected on different strings, removing one position 
                    // shouldn't remove "C4" from selectedNotes if another "C4" position remains.
                    // However, we also need to handle the case where "C4" was selected via Piano (no position).
                    
                    // Re-calculate selected notes based on remaining positions + existing notes that *didn't* have positions?
                    // This gets complex mixing modes. 
                    // Simplified approach: If ANY position for this note remains, keep it. 
                    // But what if it was selected on piano?
                    
                    // Let's assume strict synchronization for now: 
                    // Note is selected if it's in `selectedNotes`.
                    // Position is highlighted if it's in `selectedGuitarPositions`.
                    
                    // If we toggle OFF a position:
                    // 1. Remove position.
                    // 2. If NO other positions for this note exist AND it wasn't explicitly selected via piano... 
                    // Actually, let's just add/remove the note name based on whether we are adding/removing the position.
                    // If adding: Ensure note name is present.
                    // If removing: Check if any other positions correspond to this note. If not, remove note name? 
                    // Or just treat them loosely coupled?
                    
                    // User Request: "plotting blue dots on every instance... instead of just in the fret position".
                    // This implies the drawing logic blindly uses `selectedNotes`.
                    // So we MUST decouple the drawing logic to use `selectedGuitarPositions` when available.
                    
                    newSelectedNotes = if (currentSelectedPositions.none { pos -> pos != guitarPosition && getNoteNameAt(pos, it) == noteName }) {
                         currentSelectedNotes - noteName
                    } else {
                         currentSelectedNotes
                    }
                } else {
                    newSelectedPositions = currentSelectedPositions + guitarPosition
                    newSelectedNotes = if (!currentSelectedNotes.contains(noteName)) currentSelectedNotes + noteName else currentSelectedNotes
                }
            } else {
                // Piano/Abstract Input Mode: Toggle note name
                newSelectedPositions = currentSelectedPositions // Don't change positions
                newSelectedNotes = if (currentSelectedNotes.contains(noteName)) {
                    currentSelectedNotes - noteName
                } else {
                    currentSelectedNotes + noteName
                }
            }

            val newItemToSet = if (newSelectedNotes.isNotEmpty()) {
                TrackItem(
                    id = uuid4().toString(),
                    beat = 0f,
                    duration = 1f, // Default 1 beat
                    type = "note",
                    data = TrackData(
                        notes = newSelectedNotes,
                        instrument = it.instrument
                    )
                )
            } else {
                null
            }

            it.copy(selectedNotes = newSelectedNotes, selectedGuitarPositions = newSelectedPositions, itemToSet = newItemToSet)
        }
    }
    
    private fun getNoteNameAt(position: Pair<Int, Int>, state: StudioUiState): String? {
        val (stringIndex, fret) = position
        val tuning = state.song?.guitarTuning ?: ChordUtils.DEFAULT_GUITAR_TUNING
        val capo = state.song?.guitarCapo ?: 0
        
        // Copied logic from getNoteForFret
        if (fret < 0) return null
        val openNote = tuning.getOrNull(stringIndex) ?: return null
        val openMidi = ChordUtils.noteNameToMidi(openNote) ?: return null
        val finalMidi = if (fret == 0) openMidi + capo else openMidi + fret
        return ChordUtils.midiToNoteName(finalMidi)
    }

    fun onSetItemClicked() {
        if (isViewer) return
        val currentState = _uiState.value
        val itemToSet = currentState.itemToSet ?: return
        val currentBeat = currentState.settings.playheadPosition

        val lengthInBeats = if (itemToSet.type == "recording") {
            itemToSet.duration.coerceAtLeast(0.1f)
        } else {
            calculateIntervalInBeats(currentState.noteLength, currentState.settings.bpm)
        }

        val itemToAddTemplate = if (itemToSet.type == "recording") {
            itemToSet.copy(originalDurationBeats = lengthInBeats)
        } else {
            itemToSet
        }

        val itemsToAdd = mutableListOf<TrackItem>()

        val intervalInBeats = calculateIntervalInBeats(currentState.noteInterval, currentState.settings.bpm)
        var totalAdvanceBeats = intervalInBeats

        if (itemToSet.type == "note" && itemToSet.data.notes != null && itemToSet.data.notes.size > 1) {
            itemToSet.data.notes.forEach { note ->
                val newNoteItem = TrackItem(
                    id = uuid4().toString(),
                    beat = currentBeat,
                    duration = lengthInBeats,
                    type = "note",
                    data = TrackData(
                        notes = listOf(note),
                        instrument = itemToSet.data.instrument,
                        name = note
                    )
                )
                itemsToAdd.add(newNoteItem)
            }
        } else if (itemToSet.type == "progression" && itemToSet.data.notes != null) {
            var placingBeat = currentBeat
            itemToSet.data.notes.forEach { chordName ->
                val newChordItem = TrackItem(
                    id = uuid4().toString(),
                    beat = placingBeat,
                    duration = lengthInBeats,
                    type = "chord",
                    data = TrackData(
                        name = chordName,
                        instrument = itemToSet.data.instrument ?: currentState.instrument
                    )
                )
                itemsToAdd.add(newChordItem)
                placingBeat += intervalInBeats
            }
            totalAdvanceBeats = itemToSet.data.notes.size * intervalInBeats
        }

        if (itemsToAdd.isEmpty()) {
            var finalData = itemToAddTemplate.data
            if (itemToSet.type == "note" && finalData.notes?.isNotEmpty() == true) {
                 finalData = finalData.copy(name = finalData.notes.joinToString(", "))
            }

            val singleItem = itemToAddTemplate.copy(
                id = uuid4().toString(),
                beat = currentBeat,
                duration = lengthInBeats,
                data = finalData
            )
            itemsToAdd.add(singleItem)
        }

        addTrackItemsInternal(itemsToAdd)

        _uiState.update {
            it.copy(
                itemToSet = null,
                selectedNotes = emptyList(),
                selectedGuitarPositions = emptySet(),
                settings = it.settings.copy(playheadPosition = currentBeat + totalAdvanceBeats)
            )
        }
    }

    // Audio
    fun onPlayRecordingClicked(recording: RecordingInfo) {
        val currentPlayingId = _uiState.value.playingRecordingId

        if (currentPlayingId == recording.id) {
            // Stop
            audioHandler.stopPlayback()
            _uiState.update { it.copy(playingRecordingId = null) }
        } else {
            // Stop previous if any
            if (currentPlayingId != null) {
                audioHandler.stopPlayback()
            }

            // Start new
            _uiState.update { it.copy(playingRecordingId = recording.id) }

            audioHandler.playAudio(recording.url) {
                // On completion
                _uiState.update {
                    if (it.playingRecordingId == recording.id) {
                        it.copy(playingRecordingId = null)
                    } else it
                }
            }
        }
    }

    // Track Controls
    fun onTrackMuteChanged(trackId: String, isMuted: Boolean) {
        if (isViewer) return
        _uiState.update { state ->
            val updatedTracks = state.tracks.map { if (it.id == trackId) it.copy(isMuted = isMuted) else it }
            state.copy(tracks = updatedTracks)
        }
        // markAsTrackChanged(trackId) // Removed to keep Mute local
    }
    fun onTrackSoloChanged(trackId: String, isSoloed: Boolean) {
        if (isViewer) return
        _uiState.update { state ->
            val updatedTracks = state.tracks.map { if (it.id == trackId) it.copy(isSoloed = isSoloed) else it }
            state.copy(tracks = updatedTracks)
        }
        // markAsTrackChanged(trackId) // Removed to keep Solo local
    }
    fun onTrackVolumeChanged(trackId: String, volume: Float) {
        if (isViewer) return
        _uiState.update { state ->
            val updatedTracks = state.tracks.map { if (it.id == trackId) it.copy(volume = volume) else it }
            state.copy(tracks = updatedTracks)
        }
        markAsTrackChanged(trackId)
    }
    fun onTrackStrumSpeedChanged(trackId: String, speed: Float) {
        if (isViewer) return
        _uiState.update { state ->
            val updatedTracks = state.tracks.map { if (it.id == trackId) it.copy(strumSpeed = speed) else it }
            state.copy(tracks = updatedTracks)
        }
        markAsTrackChanged(trackId)
    }
    fun onToggleTrackCollapse(trackId: String) {
        if (isViewer) return
        _uiState.update { state ->
            val updatedTracks = state.tracks.map { if (it.id == trackId) it.copy(isCollapsed = !it.isCollapsed) else it }
            state.copy(tracks = updatedTracks)
        }
        markAsTrackChanged(trackId)
    }

    // Helper function to calculate duration based on settings
    private fun calculateIntervalInBeats(interval: StepInterval, bpm: Int): Float {
        val beatsPerBar = _uiState.value.settings.timeSignature.beats
        val ratio = interval.numerator.toFloat() / interval.denominator.toFloat()
        return if (interval.base == "bar") {
            ratio * beatsPerBar
        } else {
            ratio
        }
    }

    /**
     * Plays a list of notes as a strum.
     */
    fun playNotesAsStrum(
        notes: List<String>,
        instrument: String,
        strumDelayMs: Long,
        volume: Float = 0.8f
    ) {
        val actualNotes = if ((notes as Any) is String) {
            (notes as Any as String).split(",").map { it.trim() }
        } else {
            notes
        }
        if (actualNotes.isEmpty()) return

        // Play first note synchronously to satisfy Web Audio API's strict user gesture requirements
        audioHandler.playNote(actualNotes.first(), instrument, volume)

        if (actualNotes.size > 1) {
            viewModelScope.launch {
                if (strumDelayMs > 0) {
                    delay(strumDelayMs)
                }
                actualNotes.drop(1).forEach { note ->
                    audioHandler.playNote(note, instrument, volume)
                    if (strumDelayMs > 0) {
                        delay(strumDelayMs)
                    }
                }
            }
        }
    }

    fun playChordPreview(chordName: String, currentUiInstrument: String, strumSpeed: Float) {
        val song = _uiState.value.song ?: return
        val chordDef = song.chordDefinitions[chordName]

        val notesToPlay: List<String>
        val instrumentToPlay = currentUiInstrument

        if (instrumentToPlay.equals("guitar", ignoreCase = true)) {
            val tuning = song.guitarTuning
            val capo = song.guitarCapo
            val tab = chordDef?.guitarTab?.ifEmpty { null }?.map { it.toIntOrNull() ?: if (it == "x") 'x' else 0 }
               ?: ChordUtils.getTabForCapo(ChordUtils.parseChordName(chordName).first, ChordUtils.parseChordName(chordName).second, capo)
               ?: ChordUtils.GUITAR_CHORD_TABS[chordName]

            notesToPlay = if (tab != null) {
                ChordUtils.tabToNotes(tab, tuning, capo)
            } else {
                emptyList()
            }
        } else {
            notesToPlay = chordDef?.pianoNotes?.ifEmpty { null } ?: run {
               val (root, quality) = ChordUtils.parseChordName(chordName)
               ChordUtils.PIANO_CHORD_VOICINGS[chordName]
                   ?: ChordUtils.calculateChordNotes(root, quality)
            }
        }

        if (notesToPlay.isEmpty()) return

        val strumDelayMs = (strumSpeed * 120f).toLong().coerceAtLeast(10L)
        playNotesAsStrum(notesToPlay, instrumentToPlay, strumDelayMs)
    }

    // Selection / Lasso / Zoom
    fun toggleItemSelection(itemId: String) {
        onItemClicked(itemId)
    }
    fun onItemClicked(itemId: String) {
         if (isViewer) return
         _uiState.update { currentState ->
            val currentSelection = currentState.selectedTrackItemIds
            val isNewlySelected = !currentSelection.contains(itemId)
            val newSelection = if (!isNewlySelected) {
                currentSelection - itemId
            } else {
                currentSelection + itemId
            }
            
            val track = currentState.tracks.find { t -> t.items.any { it.id == itemId } }
            
            // Sync presence for visual indicator
            if (newSelection.contains(itemId)) {
                updatePresence(trackId = track?.id, trackItemId = itemId)
            } else if (newSelection.isEmpty()) {
                updatePresence(trackId = null, trackItemId = null)
            }
            
            currentState.copy(
                selectedTrackItemIds = newSelection,
                activeTrackId = if (newSelection.contains(itemId)) track?.id else currentState.activeTrackId,
                activeTrackItemId = if (newSelection.contains(itemId)) itemId else currentState.activeTrackItemId
            )
        }
    }
    fun onDeleteSelectedItems() {
        if (isViewer) return
        val currentState = _uiState.value
        val idsToDelete = currentState.selectedTrackItemIds
        if (idsToDelete.isEmpty()) return
        val itemsToDelete = currentState.tracks.flatMap { it.items }.filter { idsToDelete.contains(it.id) }
        deleteTrackItemsInternal(idsToDelete.toList())
        _uiState.update { it.copy(selectedTrackItemIds = emptySet()) }
        updatePresence(trackId = null, trackItemId = null)
    }
    fun onCopySelectedItems() {
        if (isViewer) return
        _uiState.update { currentState ->
            val idsToCopy = currentState.selectedTrackItemIds
            if (idsToCopy.isEmpty()) return@update currentState

            val playheadPos = currentState.settings.playheadPosition

            // Find all the original items to be copied
            val originalItems =
                currentState.tracks.flatMap { it.items }.filter { it.id in idsToCopy }
            if (originalItems.isEmpty()) return@update currentState

            // Find the earliest beat of the selected items to maintain relative spacing
            val earliestBeat = originalItems.minOf { it.beat }

            val newItemsByTrack = mutableMapOf<String, MutableList<TrackItem>>()
            val newSelectionIds = mutableSetOf<String>()

            // Create new copies and calculate their new positions
            originalItems.forEach { originalItem ->
                val newId = uuid4().toString()
                val beatOffset = originalItem.beat - earliestBeat
                val newItem = originalItem.copy(
                    id = newId,
                    beat = playheadPos + beatOffset
                )

                // Find which track the original item belonged to
                val targetTrackId = currentState.tracks.find { track ->
                    track.items.any { it.id == originalItem.id }
                }?.id

                if (targetTrackId != null) {
                    newItemsByTrack.getOrPut(targetTrackId) { mutableListOf() }.add(newItem)
                    newSelectionIds.add(newId)
                }
            }

            // Add the new items to their respective tracks
            val updatedTracks = currentState.tracks.map { track ->
                if (newItemsByTrack.containsKey(track.id)) {
                    track.copy(items = track.items + newItemsByTrack[track.id]!!)
                } else {
                    track
                }
            }

            // Return the new state with the copied items added and selected
            currentState.copy(
                tracks = updatedTracks,
                selectedTrackItemIds = newSelectionIds
            )
        }
        markAsChanged(forceUndoStep = true)
    }
    fun onNudgeSelectedItems(direction: Int) {
        if (isViewer) return
        _uiState.update { currentState ->
            val idsToNudge = currentState.selectedTrackItemIds
            if (idsToNudge.isEmpty()) return@update currentState

            val nudgeAmount = (calculateSnapIntervalInBeats() ?: 1f) * direction

            if (nudgeAmount == 0f) return@update currentState

            val updatedTracks = currentState.tracks.map { track ->
                track.copy(items = track.items.map { item ->
                    if (idsToNudge.contains(item.id)) {
                        item.copy(beat = (item.beat + nudgeAmount).coerceAtLeast(0f))
                    } else {
                        item
                    }
                })
            }
            currentState.copy(tracks = updatedTracks)
        }
        markAsChanged(forceUndoStep = true)
    }
    fun onCancelSelection() {
        _uiState.update { it.copy(selectedTrackItemIds = emptySet(), isLassoModeActive = false) }
        updatePresence(trackId = null, trackItemId = null)
    }
    
        fun onZoomChange(zoomX: Float, zoomY: Float) {
    
            if (zoomX.isNaN() || zoomX.isInfinite() || zoomY.isNaN() || zoomY.isInfinite()) return
    
            _uiState.update {
    
                it.copy(
    
                    settings = it.settings.copy(
    
                        zoomX = (it.settings.zoomX * zoomX).coerceIn(0.5f, 3.0f),
    
                        zoomY = (it.settings.zoomY * zoomY).coerceIn(0.5f, 3f)
    
                    )
    
                )
    
            }
    
        }
    fun onLassoDrawStart(offset: Offset) {
        if (isViewer) return
        _uiState.update { it.copy(isLassoModeActive = true, lassoPath = listOf(offset)) }
    }
    fun onLassoDraw(offset: Offset) {
        _uiState.update { it.copy(lassoPath = it.lassoPath + offset) }
    }
    fun onLassoDrawEnd(pixelsPerBeat: Float, trackHeightPx: Float) {
        _uiState.update { currentState ->
            if (!currentState.isLassoModeActive || currentState.lassoPath.size < 3) {
                return@update currentState.copy(lassoPath = emptyList())
            }

            val lassoPolygon = Path().apply {
                currentState.lassoPath.firstOrNull()?.let { moveTo(it.x, it.y) }
                currentState.lassoPath.drop(1).forEach { lineTo(it.x, it.y) }
                close()
            }

            val newlySelectedIds = mutableSetOf<String>()

            currentState.trackItemLayouts.forEach { (itemId, itemRect) ->
                val track = currentState.tracks.find { it.items.any { item -> item.id == itemId } }
                
                if (track != null && !track.isCollapsed) {
                    val corners = listOf(
                        itemRect.topLeft,
                        itemRect.topRight,
                        itemRect.bottomLeft,
                        itemRect.bottomRight
                    )

                    val bounds = lassoPolygon.getBounds()
                    val allCornersInside = corners.all { corner ->
                        bounds.contains(corner)
                    }

                    if (allCornersInside) {
                        newlySelectedIds.add(itemId)
                    }
                }
            }
            
            currentState.copy(
                selectedTrackItemIds = currentState.selectedTrackItemIds + newlySelectedIds,
                lassoPath = emptyList()
            )
        }
        markAsChanged()
    }
    fun onLassoModeToggled() {
        if (isViewer) return
        _uiState.update { 
            it.copy(
                isLassoModeActive = !it.isLassoModeActive,
                lassoPath = emptyList()
            ) 
        }
    }
    
    // Dragging
    fun onItemDragStart(itemId: String, type: DragType) {
         if (isViewer) return
         val item = _uiState.value.tracks.flatMap { it.items }.find { it.id == itemId } ?: return
         
         _uiState.update { currentState ->
             // Auto-select the dragged item if it's not already part of the selection.
             // This triggers the track lock mechanism.
             val newSelection = if (itemId !in currentState.selectedTrackItemIds) {
                 setOf(itemId) 
             } else {
                 currentState.selectedTrackItemIds
             }
             
             currentState.copy(
                 dragState = DragState(type, listOf(DragItemState(itemId, item.beat, item.duration))),
                 selectedTrackItemIds = newSelection
             )
         }
    }
    fun toggleGuitarInputMode() {
        _uiState.update {
            val newMode = if (it.guitarInputMode == GuitarInputMode.KEYBOARD) GuitarInputMode.FRETBOARD else GuitarInputMode.KEYBOARD
            it.copy(guitarInputMode = newMode)
        }
    }
    fun changeKeyboardOctave(delta: Int) {
        _uiState.update {
            val newOctave = (it.keyboardOctave + delta).coerceIn(0, 7)
            it.copy(keyboardOctave = newOctave)
        }
    }
    fun onFretWindowChange(delta: Int) {
        _uiState.update {
            val newStart = (it.fretWindowStart + delta).coerceIn(0, 18)
            it.copy(fretWindowStart = newStart)
        }
    }
    fun updateFretWindowStart(newStart: Int) {
         _uiState.update { it.copy(fretWindowStart = newStart.coerceIn(0, 18)) }
    }
    
    // Chord Creator Functions
    fun getChordCreatorUiState(): ChordCreatorUiState {
        val mainInstrument = _uiState.value.instrument
        val currentTuning = _uiState.value.song?.guitarTuning ?: ChordUtils.DEFAULT_GUITAR_TUNING
        val currentCapo = _uiState.value.song?.guitarCapo ?: 0
        return _uiState.value.chordCreatorUiState.copy(
            selectedInstrument = mainInstrument,
            guitarTuning = currentTuning,
            guitarCapo = currentCapo
        )
    }

    fun initChordCreator(chordName: String?) {
        val (parsedRoot, parsedQualityKey) = ChordUtils.parseChordName(chordName ?: "")
        
        _uiState.update { currentState ->
            val currentInstrument = currentState.instrument
            val currentTuning = currentState.song?.guitarTuning ?: ChordUtils.DEFAULT_GUITAR_TUNING
            val currentCapo = currentState.song?.guitarCapo ?: 0
            val initialFretStart = (currentCapo - 1).coerceAtLeast(0)

            val chordDef = if (chordName != null) currentState.song?.chordDefinitions?.get(chordName) else null

            val initialTab: List<Any>
            val initialPianoNotes: List<String>
            val initialIsCustom: Boolean
            val initialRoot: String
            val initialQuality: String

            if (chordDef != null && chordDef.isCustom) {
                initialPianoNotes = chordDef.pianoNotes ?: emptyList()
                initialIsCustom = true
                initialRoot = ""
                initialQuality = ""
                initialTab = chordDef.guitarTab?.map { fretString ->
                    fretString.toIntOrNull() ?: if (fretString == "x") 'x' else 0
                } ?: listOf('x', 'x', 'x', 'x', 'x', 'x')
            } else if (chordName != null) {
                val root = chordDef?.root ?: parsedRoot
                val qualityKey = chordDef?.quality ?: parsedQualityKey
                
                initialPianoNotes = ChordUtils.PIANO_CHORD_VOICINGS[chordName]
                    ?: ChordUtils.calculateChordNotes(root, qualityKey)

                val relativeTabShape = ChordUtils.getTabForCapo(root, qualityKey, currentCapo)
                    ?: listOf('x', 'x', 'x', 'x', 'x', 'x')
                initialTab = relativeTabShape.map { fret ->
                    when (fret) {
                        is Int -> if (fret > 0) fret + currentCapo else currentCapo
                        else -> fret
                    }
                }

                initialIsCustom = false
                initialRoot = root
                initialQuality = qualityKey
            } else {
                val (defaultRoot, defaultQualityKey) = "C" to "maj"
                val targetChordName = ChordUtils.getChordName(defaultRoot, defaultQualityKey)

                initialPianoNotes = ChordUtils.PIANO_CHORD_VOICINGS[targetChordName]
                    ?: ChordUtils.calculateChordNotes(defaultRoot, defaultQualityKey)

                val relativeTabShape = ChordUtils.getTabForCapo(defaultRoot, defaultQualityKey, currentCapo)
                    ?: listOf('x', 'x', 'x', 'x', 'x', 'x')
                initialTab = relativeTabShape.map { fret ->
                    when (fret) {
                        is Int -> if (fret > 0) fret + currentCapo else currentCapo
                        else -> fret
                    }
                }

                initialIsCustom = false
                initialRoot = defaultRoot
                initialQuality = defaultQualityKey
            }

            currentState.copy(
                fretWindowStart = initialFretStart,
                chordCreatorUiState = currentState.chordCreatorUiState.copy(
                    selectedInstrument = currentInstrument,
                    guitarTuning = currentTuning,
                    guitarCapo = currentCapo,
                    selectedChordRoot = initialRoot,
                    selectedChordQuality = initialQuality,
                    currentTab = initialTab,
                    manualKeys = initialPianoNotes,
                    isCustom = initialIsCustom,
                    keyboardOctave = if (currentInstrument == "bass") 2 else 4
                )
            )
        }
        checkIfChordIsFavorited()
    }

    fun playCurrentChordInCreator() {
        val creatorState = _uiState.value.chordCreatorUiState
        val instrument = creatorState.selectedInstrument
        val notesToPlay: List<String>

        if (instrument.equals("piano", ignoreCase = true)) {
            notesToPlay = if (creatorState.isCustom) {
                creatorState.manualKeys
            } else {
                val chordName = ChordUtils.getChordName(
                    creatorState.selectedChordRoot,
                    creatorState.selectedChordQuality
                )
                ChordUtils.PIANO_CHORD_VOICINGS[chordName] ?: ChordUtils.calculateChordNotes(creatorState.selectedChordRoot, creatorState.selectedChordQuality)
            }
        } else {
            notesToPlay = ChordUtils.tabToNotes(
                creatorState.currentTab,
                creatorState.guitarTuning,
                creatorState.guitarCapo
            )
        }

        if (notesToPlay.isEmpty()) return

        val strumDelayMs = (_strumSpeed.value * 0.83f).toLong().coerceAtLeast(10L)
        playNotesAsStrum(notesToPlay, instrument, strumDelayMs)
    }

    fun shuffleChordSelection(){
        val randomRoot = ChordUtils.ROOT_NOTES.random()
        val randomQualityKey = ChordUtils.CHORD_QUALITIES.keys.random()
        val newCategoryIndex = findCategoryIndexForQuality(randomQualityKey)

        onPresetChordSelected(randomRoot, randomQualityKey)

        _uiState.update {
            it.copy(
                chordCreatorUiState = it.chordCreatorUiState.copy(
                    selectedQualityCategoryIndex = newCategoryIndex
                )
            )
        }
        playChordPreset(randomRoot, randomQualityKey)
    }

    fun onPresetChordSelected(root: String, qualityKey: String) {
        _uiState.update { currentState ->
            val creatorState = currentState.chordCreatorUiState
            val capo = creatorState.guitarCapo
            val activeInstrument = creatorState.selectedInstrument
            val currentIsCustom = creatorState.isCustom

            val targetChordName = ChordUtils.getChordName(root, qualityKey)

            val presetPianoNotes = ChordUtils.PIANO_CHORD_VOICINGS[targetChordName]
                ?: ChordUtils.calculateChordNotes(root, qualityKey)
            val presetRelativeTab = ChordUtils.getTabForCapo(root, qualityKey, capo)
                ?: listOf('x', 'x', 'x', 'x', 'x', 'x')
            val presetAbsoluteTab = presetRelativeTab.map { fret ->
                when (fret) {
                    is Int -> if (fret > 0) fret + capo else capo
                    else -> fret
                }
            }

            val lowestFret = presetRelativeTab
                .filterIsInstance<Int>()
                .filter { it > 0 }
                .minOrNull() ?: 0

            val newFretWindowStart = (lowestFret - 1).coerceAtLeast(0)

            val finalPianoNotes: List<String>
            val finalGuitarTab: List<Any>
            val newIsCustom: Boolean

            if (!currentIsCustom) {
                finalPianoNotes = presetPianoNotes
                finalGuitarTab = presetAbsoluteTab
                newIsCustom = false
            } else {
                val currentPianoNotes = creatorState.manualKeys
                val currentGuitarTab = creatorState.currentTab

                finalPianoNotes = if (activeInstrument.equals("piano", ignoreCase = true)) {
                    presetPianoNotes
                } else {
                    currentPianoNotes
                }

                finalGuitarTab = if (activeInstrument.equals("guitar", ignoreCase = true)) {
                    presetAbsoluteTab
                } else {
                    currentGuitarTab
                }

                val pianoMatches = (finalPianoNotes == presetPianoNotes)
                val guitarMatches = (finalGuitarTab == presetAbsoluteTab)
                newIsCustom = !(pianoMatches && guitarMatches)
            }

            currentState.copy(
                fretWindowStart = newFretWindowStart,
                chordCreatorUiState = currentState.chordCreatorUiState.copy(
                    isCustom = newIsCustom,
                    selectedChordRoot = root,
                    selectedChordQuality = qualityKey,
                    manualKeys = finalPianoNotes,
                )
            )
        }
        checkIfChordIsFavorited()
    }

    fun playChordPreset(root: String, qualityKey: String) {
        playCurrentChordInCreator()
    }

    fun selectQualityCategory(index: Int) {
        _uiState.update {
            it.copy(
                chordCreatorUiState = it.chordCreatorUiState.copy(
                    selectedQualityCategoryIndex = index
                )
            )
        }
        checkIfChordIsFavorited()
    }

    private fun findCategoryIndexForQuality(qualityKey: String): Int {
        val index = ChordUtils.CHORD_QUALITY_CATEGORIES.indexOfFirst { category ->
            category.qualities.contains(qualityKey)
        }
        return if (index == -1) 0 else index
    }

    fun setKeyboardOctave(delta: Int) {
        _uiState.update {
            val currentOctave = it.chordCreatorUiState.keyboardOctave
            val newOctave = (currentOctave + delta).coerceIn(0, 7)
            it.copy(chordCreatorUiState = it.chordCreatorUiState.copy(keyboardOctave = newOctave))
        }
    }

    fun clearManualKeys() {
        _uiState.update {
            it.copy(chordCreatorUiState = it.chordCreatorUiState.copy(manualKeys = emptyList()))
        }
    }

    fun toggleManualKey(noteName: String) {
        _uiState.update { currentState ->
            val creatorState = currentState.chordCreatorUiState
            val root = creatorState.selectedChordRoot
            val qualityKey = creatorState.selectedChordQuality

            val baseKeys = if (creatorState.isCustom) {
                creatorState.manualKeys
            } else {
                val chordName = ChordUtils.getChordName(root, qualityKey)
                ChordUtils.PIANO_CHORD_VOICINGS[chordName] ?: emptyList()
            }
            val newKeys = if (baseKeys.contains(noteName)) {
                baseKeys - noteName
            } else {
                baseKeys + noteName
            }

            if (newKeys.isNotEmpty()) {
                val strumDelayMs = (_strumSpeed.value * 0.83f).toLong().coerceAtLeast(10L)
                playNotesAsStrum(newKeys, "piano", strumDelayMs)
            }

            currentState.copy(
                chordCreatorUiState = currentState.chordCreatorUiState.copy(
                    manualKeys = newKeys,
                    isCustom = true,
                    selectedChordRoot = "",
                    selectedChordQuality = ""
                )
            )
        }
        checkIfChordIsFavorited()
    }

    fun onTabChange(newTab: List<Any>) {
        _uiState.update { currentState ->
            val creatorState = currentState.chordCreatorUiState
            val newNotes = ChordUtils.tabToNotes(
                newTab,
                creatorState.guitarTuning,
                creatorState.guitarCapo
            )

            val strumDelayMs = (_strumSpeed.value * 0.83f).toLong().coerceAtLeast(10L)
            playNotesAsStrum(newNotes, "guitar", strumDelayMs)

            currentState.copy(
                chordCreatorUiState = currentState.chordCreatorUiState.copy(
                    currentTab = newTab,
                    isCustom = true,
                    selectedChordRoot = "",
                    selectedChordQuality = ""
                )
            )
        }
        checkIfChordIsFavorited()
    }

    fun onCustomToggle() {
        _uiState.update { currentState ->
            val creatorState = currentState.chordCreatorUiState
            val newState = !creatorState.isCustom

            if (newState) {
                currentState.copy(
                    chordCreatorUiState = creatorState.copy(
                        isCustom = true,
                        selectedChordRoot = "",
                        selectedChordQuality = ""
                    )
                )
            } else {
                val root = creatorState.selectedChordRoot.ifBlank { "C" }
                val qualityKey = creatorState.selectedChordQuality.ifBlank { "maj" }
                val capo = creatorState.guitarCapo
                val targetChordName = ChordUtils.getChordName(root, qualityKey)

                val presetPianoNotes = ChordUtils.PIANO_CHORD_VOICINGS[targetChordName]
                    ?: ChordUtils.calculateChordNotes(root, qualityKey)
                val presetRelativeTab = ChordUtils.getTabForCapo(root, qualityKey, capo)
                    ?: listOf('x', 'x', 'x', 'x', 'x', 'x')
                val presetAbsoluteTab = presetRelativeTab.map { fret ->
                    when (fret) {
                        is Int -> if (fret > 0) fret + capo else capo
                        else -> fret
                    }
                }

                currentState.copy(
                    chordCreatorUiState = creatorState.copy(
                        isCustom = false,
                        selectedChordRoot = root,
                        selectedChordQuality = qualityKey,
                        manualKeys = presetPianoNotes,
                        currentTab = presetAbsoluteTab
                    )
                )
            }
        }
    }

    fun onTuningChange(stringIndex: Int, step: Int) {
        val currentState = _uiState.value
        val creatorState = currentState.chordCreatorUiState
        val currentTuning = creatorState.guitarTuning.toMutableList()
        val currentNote = currentTuning.getOrNull(stringIndex) ?: return

        val currentIndex = ChordUtils.ALL_NOTES.indexOf(currentNote)
        if (currentIndex == -1) return

        val newIndex = (currentIndex + step).coerceIn(0, ChordUtils.ALL_NOTES.size - 1)
        val newNote = ChordUtils.ALL_NOTES[newIndex]
        currentTuning[stringIndex] = newNote
        val newTuningList = currentTuning.toList()

        val recalculatedNotes = ChordUtils.tabToNotes(
            creatorState.currentTab,
            newTuningList,
            creatorState.guitarCapo
        )

        audioHandler.playNote(newNote, "guitar")

        _uiState.update {
            it.copy(
                chordCreatorUiState = it.chordCreatorUiState.copy(
                    guitarTuning = newTuningList,
                    manualKeys = recalculatedNotes
                )
            )
        }
        checkIfChordIsFavorited()
        markAsChanged()
    }

    fun onCapoChange(newCapo: Int) {
        val capo = newCapo.coerceIn(0, 12)
        
        _uiState.update { currentState ->
            val oldCapo = currentState.chordCreatorUiState.guitarCapo
            val capoDelta = capo - oldCapo
            if (capoDelta == 0) return@update currentState

            val currentTuning = currentState.chordCreatorUiState.guitarTuning
            val currentIsCustom = currentState.chordCreatorUiState.isCustom
            val currentRoot = currentState.chordCreatorUiState.selectedChordRoot
            val currentQuality = currentState.chordCreatorUiState.selectedChordQuality
            val originalTab = currentState.chordCreatorUiState.currentTab

            val newFretWindowStart = (capo - 1).coerceAtLeast(0)
            var newAbsoluteTab: List<Any>
            var newManualKeys: List<String>
            var newIsCustom: Boolean
            var newRoot: String
            var newQuality: String
            var localNotesToPlay: List<String>

            if (!currentIsCustom && currentRoot.isNotBlank()) {
                val relativeTabShape = ChordUtils.getTabForCapo(currentRoot, currentQuality, capo)
                if (relativeTabShape != null) {
                    newAbsoluteTab = relativeTabShape.map { fret ->
                        when (fret) {
                            is Int -> if (fret > 0) fret + capo else capo
                            else -> fret
                        }
                    }
                    newManualKeys = ChordUtils.tabToNotes(newAbsoluteTab, currentTuning, 0)
                    localNotesToPlay = newManualKeys
                    newIsCustom = false
                    newRoot = currentRoot
                    newQuality = currentQuality
                } else {
                    newAbsoluteTab = listOf('x', 'x', 'x', 'x', 'x', 'x')
                    newManualKeys = emptyList()
                    localNotesToPlay = emptyList()
                    newIsCustom = currentIsCustom
                    newRoot = currentRoot
                    newQuality = currentQuality
                }
            } else {
                newAbsoluteTab = originalTab.map { fret ->
                    when (fret) {
                        is Int -> {
                            val potentiallyNewAbsoluteFret = fret + capoDelta
                            if (potentiallyNewAbsoluteFret > capo || potentiallyNewAbsoluteFret == 0) {
                                potentiallyNewAbsoluteFret.coerceAtLeast(0)
                            } else {
                                capo
                            }
                        }
                        else -> fret
                    }
                }
                newManualKeys = ChordUtils.tabToNotes(newAbsoluteTab, currentTuning, 0)
                localNotesToPlay = newManualKeys
                newIsCustom = true
                newRoot = ""
                newQuality = ""
            }

            if (localNotesToPlay.isNotEmpty()) {
                playNotesAsStrum(localNotesToPlay, "guitar", 40)
            }

            currentState.copy(
                chordCreatorUiState = currentState.chordCreatorUiState.copy(
                    guitarCapo = capo,
                    currentTab = newAbsoluteTab,
                    manualKeys = newManualKeys,
                    selectedChordRoot = newRoot,
                    selectedChordQuality = newQuality,
                    isCustom = newIsCustom
                ),
                fretWindowStart = newFretWindowStart
            )
        }
        checkIfChordIsFavorited()
        markAsChanged()
    }

    fun resetTuningToStandard() {
        _uiState.update { currentState ->
            val standardTuning = ChordUtils.DEFAULT_GUITAR_TUNING
            val currentTab = currentState.chordCreatorUiState.currentTab
            val currentCapo = currentState.chordCreatorUiState.guitarCapo
            val recalculatedNotes = ChordUtils.tabToNotes(currentTab, standardTuning, currentCapo)

            currentState.copy(
                chordCreatorUiState = currentState.chordCreatorUiState.copy(
                    guitarTuning = standardTuning,
                    manualKeys = recalculatedNotes
                )
            )
        }
        checkIfChordIsFavorited()
        markAsChanged()
    }
    
    fun onStrumSpeedChange(speed: Float) {
        _strumSpeed.value = speed
    }

    fun onShowChordCreatorSheetChange(show: Boolean) {
        _uiState.update { currentState ->
            val capo = currentState.chordCreatorUiState.guitarCapo
            val initialFretStart = (capo - 1).coerceAtLeast(0)
            currentState.copy(fretWindowStart = initialFretStart)
        }

        _showChordCreatorSheet.value = show
    }

    fun onChordToEditChange(chord: Pair<String, List<String>>?) {
        _chordToEdit.value = chord
    }

    fun cancelLoading() {
        _uiState.update { it.copy(isLoadingSamples = false) }
        // We don't cancel the background jobs (sync/preload) as they might recover,
        // but we unblock the UI.
    }

    private fun preloadAudioSamplesAsync() {
        if (_uiState.value.isLoadingSamples) return

        viewModelScope.launch(Dispatchers.Default) {
            _uiState.update { it.copy(isLoadingSamples = true, sampleLoadingProgress = 0f) }

            val requiredPaths = mutableSetOf<String>()
            val currentTracks = _uiState.value.tracks
            val song = _uiState.value.song

            currentTracks.forEach { track ->
                track.items.forEach { item ->
                    when (item.type) {
                        "note" -> {
                            item.data.notes?.forEach { noteName ->
                                requiredPaths.add("${item.data.instrument}/$noteName.mp3")
                            }
                        }
                        "chord" -> {
                            val chordDef = song?.chordDefinitions?.get(item.data.name)
                            if (chordDef != null) {
                                val trackInstrument = item.data.instrument
                                if (trackInstrument.equals("guitar", ignoreCase = true)) {
                                    chordDef.guitarTab?.let { tab ->
                                        if (song != null) {
                                            val notes = ChordUtils.tabToNotes(
                                                tab.map { it.toIntOrNull() ?: if (it == "x") 'x' else 0 },
                                                song.guitarTuning,
                                                song.guitarCapo
                                            )
                                            notes.forEach { noteName ->
                                                requiredPaths.add("guitar/$noteName.mp3")
                                            }
                                        }
                                    }
                                } else {
                                    chordDef.pianoNotes?.forEach { noteName ->
                                        requiredPaths.add("$trackInstrument/$noteName.mp3")
                                    }
                                }
                            }
                        }
                    }
                }
            }

            if (requiredPaths.isEmpty()) {
                _uiState.update { it.copy(isLoadingSamples = false, sampleLoadingProgress = 1f) }
                withContext(Dispatchers.Main) {
                    initializePlaybackEngine()
                    playbackEngine?.warmUp()
                }
                return@launch
            }

            val pathsToLoad = requiredPaths.toList()
            pathsToLoad.forEachIndexed { index, path ->
                if (!isActive) return@forEachIndexed
                AudioSampleLoader.loadSoundFromCache(path)
                val progress = (index + 1).toFloat() / pathsToLoad.size.toFloat()
                _uiState.update { it.copy(sampleLoadingProgress = progress) }
                yield()
            }

            _uiState.update { it.copy(isLoadingSamples = false, sampleLoadingProgress = 1f) }
            withContext(Dispatchers.Main) {
                initializePlaybackEngine()
                playbackEngine?.warmUp()
            }
        }
    }

    fun onSeekNextClicked() {
        _uiState.update { currentState ->
            val intervalInBeats = calculateIntervalInBeats(currentState.noteInterval, currentState.settings.bpm)
            val newPosition = currentState.settings.playheadPosition + intervalInBeats
            currentState.copy(settings = currentState.settings.copy(playheadPosition = newPosition))
        }
    }

    fun onSeekPreviousClicked() {
        _uiState.update { currentState ->
            val intervalInBeats = calculateIntervalInBeats(currentState.noteInterval, currentState.settings.bpm)
            val newPosition = (currentState.settings.playheadPosition - intervalInBeats).coerceAtLeast(0f)
            currentState.copy(settings = currentState.settings.copy(playheadPosition = newPosition))
        }
    }

    fun onClearSelectedNotes() {
        _uiState.update {
            it.copy(
                selectedNotes = emptyList(),
                selectedGuitarPositions = emptySet(),
                itemToSet = null
            )
        }
    }

    fun onItemDragEnd(dragOffsetPx: Float, pixelsPerBeat: Float) {
        val drag = _uiState.value.dragState ?: return
        if (pixelsPerBeat <= 0f) {
            _uiState.update { it.copy(dragState = null) }
            return
        }

        val deltaBeats = dragOffsetPx / pixelsPerBeat
        val snapInterval = calculateSnapIntervalInBeats()

        when (drag.type) {
            DragType.MOVE -> {
                val dragItem = drag.items.first()
                val potentialNewBeat = dragItem.initialBeat + deltaBeats
                val snappedBeat = if (snapInterval != null) (potentialNewBeat / snapInterval).roundToInt() * snapInterval else potentialNewBeat
                val finalBeat = snappedBeat.coerceAtLeast(0f)
                val beatChange = finalBeat - dragItem.initialBeat

                if (kotlin.math.abs(beatChange) > 0.001f) {
                                moveTrackItemsInternal(drag.items.map { it.id }, beatChange)
                }
            }
            
            DragType.RESIZE_START -> {
                val dragItem = drag.items.first()
                val item = _uiState.value.tracks.flatMap { it.items }.find { it.id == dragItem.id } ?: return
                
                val potentialNewBeat = dragItem.initialBeat + deltaBeats
                val snappedBeat = if (snapInterval != null) (potentialNewBeat / snapInterval).roundToInt() * snapInterval else potentialNewBeat
                
                val minBeat = if (item.type == "recording") {
                    dragItem.initialBeat - item.trimStartBeats
                } else {
                    0f
                }
                val endBeat = dragItem.initialBeat + item.duration
                
                val actualStartBeat = snappedBeat.coerceIn(minBeat, endBeat - 0.1f)
                
                val beatChange = actualStartBeat - dragItem.initialBeat
                val durationChange = dragItem.initialBeat + item.duration - actualStartBeat - item.duration // simplifies to -beatChange, but let's be explicit with actual changes
                val finalDurationChange = -beatChange
                
                val newBeat = item.beat + beatChange
                val newDuration = item.duration + finalDurationChange
                val minDuration = snapInterval ?: 0.1f

                if (newBeat >= 0 && newDuration >= minDuration) {
                    if (item.type == "recording") {
                        val newTrimStart = item.trimStartBeats + beatChange
                        if (newTrimStart >= 0) {
                            resizeTrackItemInternal(item.id, beatChange, finalDurationChange, beatChange)
                        }
                    } else {
                        resizeTrackItemInternal(item.id, beatChange, finalDurationChange, 0f)
                    }
                }
            }

            DragType.RESIZE_END -> {
                val dragItem = drag.items.first()
                val item = _uiState.value.tracks.flatMap { it.items }.find { it.id == dragItem.id } ?: return
                
                val potentialNewDuration = dragItem.initialDuration + deltaBeats
                val snappedDuration = if (snapInterval != null) (potentialNewDuration / snapInterval).roundToInt() * snapInterval else potentialNewDuration
                
                var finalDuration = snappedDuration
                if (item.type == "recording" && item.originalDurationBeats != null) {
                    val maxAllowedDuration = item.originalDurationBeats - item.trimStartBeats
                    finalDuration = finalDuration.coerceIn(snapInterval ?: 0.1f, maxAllowedDuration)
                } else {
                    finalDuration = finalDuration.coerceAtLeast(snapInterval ?: 0.1f)
                }

                val durationChange = finalDuration - dragItem.initialDuration
                if (kotlin.math.abs(durationChange) > 0.001f) {
                                resizeTrackItemInternal(item.id, 0f, durationChange, 0f)
                }
            }
        }
        _uiState.update { it.copy(dragState = null) }
        updatePresence()
    }

    fun onTrackItemLayout(itemId: String, rect: Rect) {
        _uiState.update { it.copy(trackItemLayouts = it.trackItemLayouts + (itemId to rect)) }
    }

    fun onSaveChord(customName: String) {
        if (isViewer) return
        val currentState = _uiState.value
        val creatorState = currentState.chordCreatorUiState
        val chordToEdit = _chordToEdit.value

        // 1. Create the new definition
        val newChordDef = ChordDefinition(
            name = customName,
            root = creatorState.selectedChordRoot,
            quality = creatorState.selectedChordQuality,
            pianoNotes = creatorState.manualKeys,
            guitarTab = creatorState.currentTab.map { it.toString() },
            isCustom = creatorState.isCustom
        )

        // 2. Perform the update internally
        onSaveChordInternal(chordToEdit?.first, newChordDef)
    }

    private fun onSaveChordInternal(oldName: String?, newDefinition: ChordDefinition) {
        val currentState = _uiState.value
        val currentSong = currentState.song ?: return
        val currentSettings = currentState.settings

        // Capture current Studio State to prevent overwriting with stale data
        val studioState = StudioState(
            tracks = currentState.tracks,
            timeSignature = currentSettings.timeSignature,
            isMetronomeEnabled = currentState.isMetronomeOn,
            isSnapEnabled = currentSettings.isSnappingOn,
            snapInterval = currentSettings.snapInterval
        )
        
        val updatedDefinitions = currentSong.chordDefinitions.toMutableMap()
        var updatedSections = currentSong.sections

        if (oldName != null && oldName != newDefinition.name) {
            // RENAME CASE
            val regex = """\[${Regex.escape(oldName)}\]""".toRegex()
            updatedSections = currentSong.sections.map { section ->
                section.copy(lines = section.lines.map { line ->
                    line.copy(text = line.text.replace(regex, "[${newDefinition.name}]"))
                })
            }
            updatedDefinitions.remove(oldName)
        }

        updatedDefinitions[newDefinition.name] = newDefinition

        val updatedSong = currentSong.copy(
            sections = updatedSections,
            chordDefinitions = updatedDefinitions,
            studioState = studioState,
            lastModified = Clock.System.now()
        )

        _uiState.update { 
            it.copy(song = updatedSong)
        }
        markAsChanged(forceUndoStep = true)
    }

    fun deleteChord(chordName: String) {
        if (isViewer) return
        val currentState = _uiState.value
        val currentSong = currentState.song ?: return
        val currentSettings = currentState.settings

        // 1. Remove chord from arrangement tracks
        val changedTrackIds = mutableSetOf<String>()
        val updatedTracks = currentSong.studioState?.tracks?.map { track ->
            val newItems = track.items.filterNot { it.type == "chord" && it.data.name == chordName }
            if (newItems.size != track.items.size) {
                changedTrackIds.add(track.id)
            }
            track.copy(items = newItems)
        } ?: currentState.tracks.map { track ->
            val newItems = track.items.filterNot { it.type == "chord" && it.data.name == chordName }
            if (newItems.size != track.items.size) {
                changedTrackIds.add(track.id)
            }
            track.copy(items = newItems)
        }

        val studioState = StudioState(
            tracks = updatedTracks,
            timeSignature = currentSettings.timeSignature,
            isMetronomeEnabled = currentState.isMetronomeOn,
            isSnapEnabled = currentSettings.isSnappingOn,
            snapInterval = currentSettings.snapInterval
        )

        val updatedDefinitions = currentSong.chordDefinitions.toMutableMap()
        updatedDefinitions.remove(chordName)

        val updatedSong = currentSong.copy(
            chordDefinitions = updatedDefinitions,
            studioState = studioState,
            lastModified = Clock.System.now()
        )

        _uiState.update { 
            it.copy(
                song = updatedSong, 
                tracks = updatedTracks
            )
        }
        // Clear selection if deleted
        if (_chordToEdit.value?.first == chordName) {
            onChordToEditChange(null)
        }
        markAsChanged(forceUndoStep = true)
        
        // Reset Tracking because we just forced a full save/crdt update
        modifiedTrackIds.clear()
    }

    // Settings
    fun onBpmChange(bpm: Int) {
        if (isViewer) return
        val currentBpm = _uiState.value.settings.bpm
        if (currentBpm == bpm) return

        // PRO STABILITY
        shieldField("song_bpm")

        // 1. Immediate Visual Update
        _uiState.update { it.copy(settings = it.settings.copy(bpm = bpm)) }

        // 2. Debounced Save
        bpmDebounceJob?.cancel()
        bpmDebounceJob = viewModelScope.launch {
            delay(500)
            markAsChanged()
        }
    }
    fun onTimeSignatureChange(beats: Int, per: Int) {
        if (isViewer) return
        val currentSig = _uiState.value.settings.timeSignature
        if (currentSig.beats == beats && currentSig.per == per) return

        _uiState.update {
            it.copy(settings = it.settings.copy(timeSignature = TimeSignature(beats, per)))
        }
        markAsChanged()
    }    fun onMetronomeToggle(isOn: Boolean) {
        // Metronome is playback, allowed? It's saved in StudioState. So it's an edit.
        if (isViewer) return
        _uiState.update { it.copy(isMetronomeOn = isOn) }
        // Workspace preference: Do not mark as changed
    }

    fun toggleMetronomeMuted() {
        val newMute = !_uiState.value.isMetronomeMuted
        _uiState.update { it.copy(isMetronomeMuted = newMute) }
        audioHandler.setMetronomeMuted(newMute)
    }

    fun updateMetronomeBeat(beat: Int) {
        _uiState.update { it.copy(currentMetronomeBeat = beat) }
    }

    fun onSnappingToggle(isOn: Boolean) {
        if (isViewer) return
        _uiState.update { it.copy(settings = it.settings.copy(isSnappingOn = isOn)) }
        // Workspace preference: Do not mark as changed
    }

    fun onEditIntervalClicked(type: StepIntervalType) {
        _uiState.update { it.copy(editingInterval = type) }
    }
    fun onDismissIntervalEditor() {
        _uiState.update { it.copy(editingInterval = null) }
    }
    fun onIntervalChanged(type: StepIntervalType, newInterval: StepInterval) {
        _uiState.update {
            when (type) {
                StepIntervalType.LENGTH -> it.copy(noteLength = newInterval)
                StepIntervalType.INTERVAL -> it.copy(noteInterval = newInterval)
            }
        }
    }

    fun toggleFavoriteChord(chord: ChordDefinition) {
        val currentState = _uiState.value
        val tuning = currentState.song?.guitarTuning ?: ChordUtils.DEFAULT_GUITAR_TUNING
        viewModelScope.launch {
            // Assume false for paid check for now as in EditorViewModel
            chordLibraryRepository?.toggleFavorite(chord, tuning, false)
            checkIfChordIsFavorited()
        }
    }

    private fun checkIfChordIsFavorited() {
        val currentState = _uiState.value
        val creatorState = currentState.chordCreatorUiState
        val favorites = chordLibraryRepository?.favoriteChords?.value ?: emptyList()
        val tuning = currentState.chordCreatorUiState.guitarTuning // Use creator's tuning context

        // Comprehensive check matching EditorViewModel logic
        val currentPianoNotes = creatorState.manualKeys.sorted()
        val currentGuitarTab = creatorState.currentTab.map { it.toString() }

        val isFavorited = favorites.any { fav ->
            val tuningMatches = fav.tuning == tuning
            val tabMatches = fav.chord.guitarTab == currentGuitarTab
            val notesMatches = fav.chord.pianoNotes?.sorted() == currentPianoNotes
            tuningMatches && tabMatches && notesMatches
        }
        _uiState.update { it.copy(isFavorited = isFavorited) }
    }

    fun selectFavorite(savedChord: songwritersnotebook.app.data.SavedChord?) {
        _uiState.update { 
            val newSelected = if (it.selectedFavoriteChord?.id == savedChord?.id) null else savedChord
            it.copy(selectedFavoriteChord = newSelected) 
        }
    }

    fun addFavoriteToSong(savedChord: songwritersnotebook.app.data.SavedChord) {
        val currentSong = _uiState.value.song ?: return
        val newDefs = currentSong.chordDefinitions.toMutableMap()
        newDefs[savedChord.chord.name] = savedChord.chord
        
        val updatedSong = currentSong.copy(chordDefinitions = newDefs)
        _uiState.update { it.copy(song = updatedSong) }
        markAsChanged(forceUndoStep = true)
    }

    fun removeFavorite(savedChord: songwritersnotebook.app.data.SavedChord) {
        viewModelScope.launch {
            chordLibraryRepository?.toggleFavorite(savedChord.chord, savedChord.tuning, false)
            if (_uiState.value.selectedFavoriteChord?.id == savedChord.id) {
                selectFavorite(null)
            }
            checkIfChordIsFavorited()
        }
    }

    fun onEditFavoriteClicked(savedChord: songwritersnotebook.app.data.SavedChord) {
        val def = savedChord.chord
        _uiState.update { currentState ->
            val initialFretStart = 0
            currentState.copy(
                fretWindowStart = initialFretStart,
                chordCreatorUiState = currentState.chordCreatorUiState.copy(
                    selectedInstrument = currentState.instrument,
                    guitarTuning = savedChord.tuning,
                    guitarCapo = 0,
                    selectedChordRoot = def.root ?: "",
                    selectedChordQuality = def.quality ?: "",
                    currentTab = def.guitarTab?.map { t -> t.toIntOrNull() ?: if (t == "x") 'x' else 0 } ?: List(6) { 0 },
                    manualKeys = def.pianoNotes ?: emptyList(),
                    isCustom = def.isCustom,
                    keyboardOctave = if (currentState.instrument == "bass") 2 else 4,
                    selectedQualityCategoryIndex = if (def.quality != null) {
                        ChordUtils.CHORD_QUALITY_CATEGORIES.indexOfFirst { it.qualities.contains(def.quality) }.coerceAtLeast(0)
                    } else 0
                )
            )
        }
        checkIfChordIsFavorited()
        _showChordCreatorSheet.value = true
    }

    fun playFavoriteChord(savedChord: songwritersnotebook.app.data.SavedChord) {
        val state = _uiState.value
        val instrument = state.instrument
        val chordDef = savedChord.chord
        
        val notesToPlay = if (instrument == "guitar") {
            chordDef.guitarTab?.map { it.toIntOrNull() ?: if (it == "x") 'x' else 0 }?.let {
                ChordUtils.tabToNotes(it, savedChord.tuning, 0)
            } ?: emptyList()
        } else chordDef.pianoNotes ?: emptyList()

        if (notesToPlay.isNotEmpty()) {
            val strumDelayMs = (_strumSpeed.value * 120f).toLong().coerceAtLeast(10L)
            playNotesAsStrum(notesToPlay, instrument, strumDelayMs)
        }
    }

    private fun getChordsInTracks(tracks: List<Track>): Set<String> {
        return tracks.flatMap { it.items }
            .filter { it.type == "chord" }
            .mapNotNull { it.data.name }
            .toSet()
    }

    private fun getChordsInSong(song: Song): Set<String> {
        val chordRegex = """\[([^\]]+)\]""".toRegex()
        val chords = mutableSetOf<String>()
        song.sections.forEach { section ->
            section.lines.forEach { line ->
                chordRegex.findAll(line.text).forEach { match ->
                    val chordName = match.groupValues[1]
                    if (song.chordDefinitions.containsKey(chordName)) {
                        chords.add(chordName)
                    }
                }
            }
        }
        return chords
    }

    fun clearPresenceOnExit() {
        val songId = _uiState.value.song?.id
        val userId = authUserId
        if (songId != null && userId != null) {
            songRepository?.clearRemotePresence(songId, userId, sessionDeviceId)
        }
    }

    override fun onCleared() {
        super.onCleared()

        // 1. Clear Presence Instantly
        clearPresenceOnExit()

        // Attempt final save BEFORE stopping observation
        if (hasUnsavedChanges && !isViewer) {             val currentSong = _uiState.value.song
             if (currentSong != null) {
                  val currentState = _uiState.value
                  val currentSettings = currentState.settings
                  
                  val studioState = StudioState(
                        tracks = currentState.tracks,
                        timeSignature = currentSettings.timeSignature,
                        isMetronomeEnabled = currentState.isMetronomeOn,
                        isSnapEnabled = currentSettings.isSnappingOn,
                        snapInterval = currentSettings.snapInterval,
                        zoomX = currentSettings.zoomX,
                        zoomY = currentSettings.zoomY
                  )
                  val updatedSong = currentSong.copy(studioState = studioState, bpm = currentSettings.bpm, lastModified = kotlinx.datetime.Clock.System.now())
                  songRepository?.finalSaveAndStop(
                      song = updatedSong,
                      scope = songwritersnotebook.app.crdt.UndoScope.STUDIO,
                      userId = authUserId,
                      observerId = observerId,
                      deletedSectionIds = deletedSectionIds.toList(),
                      deletedTrackIds = deletedTrackIds.toList(),
                      deletedTrackItemIds = deletedTrackItemIds.toList()
                  )
             }
        } else {
             // NOW it is safe to stop observation
             val songIdToStop = _uiState.value.song?.id
             if (songIdToStop != null) {
                 songRepository?.stopSongObservation(songIdToStop, authUserId, observerId)
             } else {
                 songRepository?.stopSongObservation(null, authUserId, observerId)
             }
        }
        
        presenceHeartbeatJob?.cancel()
        debounceSaveJob?.cancel()
        periodicSaveJob?.cancel()
        playbackJob?.cancel()
        playbackEngine?.release()
        audioHandler.release()
    }
}
